const getAuthHeaders = async (): Promise<HeadersInit> => {
  const { getSupabase } = await import("./supabase");
  const supabase = await getSupabase();
  const { data: { session } } = await supabase.auth.getSession();
  
  return {
    "Content-Type": "application/json",
    ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
  };
};

// surfaces the server's own message
const errorMessage = async (response: Response, fallback: string): Promise<string> => {
  try {
    const body = await response.json();
    if (body?.message) return body.message;
  } catch {
    // non-json body
  }
  return `${fallback} (${response.status})`;
};

export const fetchWithAuth = async <T,>(url: string, options?: RequestInit): Promise<T> => {
  const headers = await getAuthHeaders();
  const response = await fetch(url, { ...options, headers: { ...headers, ...options?.headers } });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Request failed: ${response.status}`);
  }
  if (response.status === 204) return undefined as T;
  return response.json();
};

export interface Account {
  id: string;
  smartsuite_record_id: string | null;
  account_name: string;
  is_active: boolean;
  last_synced_at: string | null;
  // Detail fields (present on /api/accounts/:id)
  entity_type?: string | null;
  ein?: string | null;
  last_4_ssn?: string | null;
  address_1?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  website?: string | null;
  formation_date?: string | null;
  s_election_date?: string | null;
  cs_client_id?: string | null;
  timezone?: string | null;
  smartvault_url?: string | null;
  sv_client_id?: string | null;
  fc_client_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface ClientNote {
  id: string;
  title: string | null;
  body: string | null;
  plain_text: string | null;
  is_pinned: boolean;
  author_user_id: string | null;
  fc_note_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface AccountRelationship {
  id: string;
  otherId: string;
  otherName: string;
  label: string;
  createdAt: string;
}

export interface AccountResource {
  id: string;
  account_id: string;
  label: string;
  url: string;
  created_at: string;
}

export interface AccountAssignee {
  userId: string;
  name: string;
  email: string;
  role: string;
  workflowRole?: string | null;
  createdAt?: string;
}

export interface AccountActivity {
  id: string;
  userId: string;
  userName: string;
  projectId: string;
  projectName: string;
  date: string;
  durationMinutes: number;
  description: string | null;
}

export interface AccountTask {
  id: string;
  name: string;
  status: string;
  dueDate: string | null;
  projectId: string;
  projectName: string;
  assignedUserName?: string | null;
}

export interface TaskWithContext {
  id: string;
  project_id: string;
  section_id: string | null;
  title: string;
  description: string | null;
  due_date: string | null;
  internal_due_date: string | null;
  status: string;
  assigned_user_id: string | null;
  is_client_task: boolean;
  allow_client_upload: boolean;
  completed_at: string | null;
  completed_by_user_id: string | null;
  list_index: number;
  budgeted_minutes: number | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  project_title: string;
  account_name: string;
  account_id: string;
  section_name: string | null;
  assigned_user_name: string | null;
  assigned_user_email: string | null;
  is_overdue: boolean;
  is_blocked?: boolean | null;
  predecessor_task_id?: string | null;
  predecessor_title?: string | null;
  financial_source_id?: string | null;
  financial_source?: { institution_name: string; account_nickname: string | null } | null;
}

export function taskWithContextToProjectTask(t: TaskWithContext): ProjectTask {
  return {
    id: t.id,
    project_id: t.project_id,
    section_id: t.section_id,
    title: t.title,
    description: t.description,
    due_date: t.due_date,
    internal_due_date: t.internal_due_date,
    status: t.status,
    assigned_user_id: t.assigned_user_id,
    is_client_task: t.is_client_task,
    allow_client_upload: t.allow_client_upload,
    completed_at: t.completed_at,
    completed_by_user_id: t.completed_by_user_id,
    list_index: t.list_index,
    budgeted_minutes: t.budgeted_minutes,
    created_at: t.created_at,
    updated_at: t.updated_at,
    deleted_at: t.deleted_at,
    predecessor_task_id: t.predecessor_task_id,
    predecessor_title: t.predecessor_title,
    is_blocked: t.is_blocked,
    financial_source_id: t.financial_source_id,
    financial_source: t.financial_source,
    users: t.assigned_user_id && t.assigned_user_name
      ? { id: t.assigned_user_id, name: t.assigned_user_name, email: t.assigned_user_email ?? "" }
      : null,
    sub_tasks: [],
    task_assignees: undefined,
    account_id: t.account_id,
    account_name: t.account_name,
    project_title: t.project_title,
  };
}

export interface ActivityCode {
  code: string;
  name: string;
  billable_default: boolean;
  is_active: boolean;
  category: string;
  source: string | null;
  source_id: string | null;
}

export interface Project {
  id: string;
  account_id: string | null;
  title: string;
  description?: string | null;
  status: string;
  start_date?: string | null;
  due_date?: string | null;
  internal_due_date?: string | null;
  billing_rate?: number | null;
  template_id?: string | null;
  created_at?: string;
  deleted_at?: string | null;
  accounts?: { id: string; account_name: string } | null;
  account_name?: string;
  logged_hours?: number;
  assigned_users?: ProjectUser[];
  recurring_pattern?: string | null;
  last_spawned_at?: string | null;
  next_spawn_date?: string | null;
  service_item_id?: string | null;
  service_item_name?: string | null;
  resources?: Array<{ label: string; url: string; type?: string; list_index?: number }>;
  computed_accounting_period?: string | null;
  accounting_period?: string | null;
  accounting_period_date?: string | null;
  workflow_status_id?: string | null;
  pre_interrupt_status_id?: string | null;
  project_type?: string | null;
  tax_year?: number | null;
  period_label?: string | null;
  // Display-ready period ("Jul 2026") computed server-side by projectPeriodLabel — render this,
  // never parseInt(period_label) (quarterly labels like "Q3 '26" would come out as January).
  period_display?: string | null;
  project_tags?: Tag[];
  client_tasks_last_notified_at?: string | null;
  task_assignee_ids?: string[];
  current_assignee_id?: string | null;
  /** Everyone on the current step (ENG-117). Match with isCurrentAssignee(). */
  current_assignee_ids?: string[];
  current_step_title?: string | null;
  next_task_due_date?: string | null;
  firm_task_total?: number;
  firm_task_done?: number;
  client_task_total?: number;
  client_task_done?: number;
  budget_minutes?: number;
  actual_minutes?: number;
  comment_count?: number;
  total_tasks?: number;
  completed_tasks?: number;
}

export interface ProjectUser {
  id: string;
  user_id: string;
  role: string;
  users?: { id: string; name: string; email: string } | null;
}

export interface ProjectRole {
  id: string;
  company_id: string;
  name: string;
  created_at: string;
}

export interface Tag {
  id: string;
  name: string;
  color: string;
  description: string | null;
  is_client_facing: boolean;
  should_repeat: boolean;
  permanent: boolean;
  usage_count?: number;
  created_at: string;
}

export interface StatusTagRule {
  id: string;
  workflow_status_id: string;
  tag_id: string;
  action: "add" | "remove";
  created_at: string;
}

export interface WorkflowStatus {
  id: string;
  pipeline_id: string;
  name: string;
  sequence_order: number;
  status_type: "normal" | "interrupt" | "terminal";
  notify_workflow_role: string | null;
  color: string;
}

export interface WorkflowPipeline {
  id: string;
  name: string;
  description: string | null;
  stages: WorkflowStatus[];
}

export interface ProjectTemplate {
  id: string;
  company_id: string;
  title: string;
  description?: string | null;
  default_duration_days?: number | null;
  recurring_pattern?: string | null;
  default_billing_rate?: string | null;
  is_active: boolean;
  workflow_pipeline_id?: string | null;
  project_type?: string | null;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
  task_count?: number;
  sections?: TaskSection[];
  tasks?: TaskTemplate[];
}

export interface ProjectTemplateInput {
  title: string;
  description?: string | null;
  default_duration_days?: number | null;
  recurring_pattern?: string | null;
  default_billing_rate?: number | null;
  is_active?: boolean;
  workflow_pipeline_id?: string | null;
  project_type?: string | null;
}

export interface TaskSection {
  id: string;
  project_template_id?: string | null;
  project_id?: string | null;
  name: string;
  section_type: string;
  is_client_facing: boolean;
  list_index: number;
  is_sequential?: boolean; // migration 096: tasks must be done in order
  created_at: string;
  updated_at: string;
}

export interface TaskSectionInput {
  name: string;
  section_type?: string;
  is_client_facing?: boolean;
  list_index?: number;
  is_sequential?: boolean;
}

export interface SectionReminderConfig {
  id: string;
  project_id: string;
  section_id: string;
  days: number;
  is_active: boolean;
  contacts: string[];
  last_sent_at: string | null;
  next_send_at: string | null;
  schedule_type: "interval" | "monthly" | "section";
  monthly_day: number | null;
  trigger_section_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface TaskTemplate {
  id: string;
  project_template_id: string;
  section_id?: string | null;
  title: string;
  description?: string | null;
  template_date_constraint?: string | null;
  relative_days?: number | null;
  default_assignee_role?: string | null;
  project_role_id?: string | null;
  is_client_task: boolean;
  allow_client_upload: boolean;
  list_index: number;
  budgeted_minutes?: number | null;
  assigned_user_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface TaskTemplateInput {
  project_template_id: string;
  section_id?: string | null;
  title: string;
  description?: string | null;
  template_date_constraint?: string;
  relative_days?: number | null;
  default_assignee_role?: string | null;
  project_role_id?: string | null;
  is_client_task?: boolean;
  allow_client_upload?: boolean;
  list_index?: number;
  budgeted_minutes?: number | null;
  assigned_user_id?: string | null;
}

export interface ProjectTask {
  id: string;
  project_id: string;
  section_id?: string | null;
  title: string;
  description?: string | null;
  due_date?: string | null;
  internal_due_date?: string | null;
  status: string;
  assigned_user_id?: string | null;
  is_client_task: boolean;
  allow_client_upload: boolean;
  completed_at?: string | null;
  completed_by_user_id?: string | null;
  list_index: number;
  budgeted_minutes?: number | null;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
  task_sections?: TaskSection | null;
  users?: { id: string; name: string; email: string } | null;
  parent_task_id?: string | null;
  sub_tasks?: ProjectTask[];
  task_assignees?: TaskAssignee[];
  financial_source_id?: string | null;
  financial_source?: { institution_name: string; account_nickname: string | null } | null;
  predecessor_task_id?: string | null;
  predecessor_title?: string | null;
  predecessor_status?: string | null;
  is_blocked?: boolean | null;
  sent_at?: string | null;
  sent_by_user_id?: string | null;
  client_task_kind?: string | null;
  visible_to_emails?: string[] | null;
  account_id?: string;
  account_name?: string;
  project_title?: string;
}

export interface TaskAssignee {
  id: string;
  task_id: string;
  user_id: string;
  list_index: number;
  users?: { id: string; name: string; email: string } | null;
}

export interface User {
  id: string;
  name: string;
  email: string;
  calendar_email?: string | null;
}

export interface ProjectMessage {
  id: string;
  project_id: string;
  author_user_id: string | null;
  author_portal_email: string | null;
  author_name: string | null;
  body: string;
  is_client_visible: boolean;
  created_at: string;
  // null = never edited (migration 100)
  edited_at?: string | null;
  users?: { id: string; name: string; email: string } | null;
  // absent on post-a-message response
  likeCount?: number;
  likedByMe?: boolean;
  likers?: string[];
}

export interface ClientTopic {
  id: string;
  account_id: string;
  name: string;
  created_at: string;
}

export interface ClientTopicMessage {
  id: string;
  topic_id: string;
  author_user_id: string | null;
  author_portal_email: string | null;
  author_name: string;
  body: string;
  channel: string; // 'web' | 'sms' | 'email'
  created_at: string;
  // null = never edited (migration 100)
  edited_at?: string | null;
  // absent on post-a-message response
  likeCount?: number;
  likedByMe?: boolean;
  likers?: string[];
}

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  avatar: string | null;
  is_active: boolean;
  confirmed_at: string | null;
  role: "staff" | "manager" | "admin" | "contractor" | "client";
  created_at: string;
  account_name?: string;
  calendar_email?: string | null;
}

export interface TimeEntry {
  id: string;
  user_id: string;
  entry_date: string;
  minutes: number;
  start_time: string | null;
  end_time: string | null;
  account_id: string | null;
  project_id: string | null;
  activity_code: string;
  notes: string | null;
  billable: boolean;
  status: "draft" | "submitted" | "approved" | "rejected";
  pay_period_id: string | null;
  review_note: string | null;
  accounts?: { id: string; account_name: string } | null;
  activity_codes?: { code: string; name: string; category: string; billable_default: boolean } | null;
  projects?: { id: string; title: string } | null;
  users?: { id: string; name: string } | null;
}

export interface PayPeriod {
  id: string;
  start_date: string;
  end_date: string;
  pay_date: string;
  status: "open" | "closed";
  closed_at: string | null;
  closed_by: string | null;
}

export interface Contact {
  id: string;
  account_id: string;
  external_id: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  phone_label: string | null;
  mobile: string | null;
  mobile_label: string | null;
  address: string | null;
  notes: string | null;
  sms_ok: boolean;
  should_remind: boolean;
  auto_sync_emails: boolean;
  show_reminder_dialog: boolean;
  contact_account_id: string | null;
  is_primary: boolean;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  accounts?: { id: string; account_name: string } | null;
}

export interface SmsLogEntry {
  id: string;
  account_id: string;
  contact_id: string | null;
  direction: "outbound" | "inbound";
  to_number: string;
  from_number: string;
  body: string;
  sent_by_user_id: string | null;
  sent_by_name: string | null;
  twilio_sid: string | null;
  status: string;
  created_at: string;
  read_at: string | null;
}

export interface SmsConversation {
  account_id: string;
  account_name: string;
  last_message_body: string;
  last_message_direction: "inbound" | "outbound";
  last_message_at: string;
  contact_name: string | null;
  unread_count: number;
}

export interface ContactInput {
  account_id: string;
  name: string;
  email?: string;
  phone?: string;
  phone_label?: string;
  mobile?: string;
  mobile_label?: string;
  address?: string;
  notes?: string;
  should_remind?: boolean;
  auto_sync_emails?: boolean;
  show_reminder_dialog?: boolean;
  is_primary?: boolean;
}

export interface TeamTimeEntry {
  id: string;
  user_id: string;
  user_name: string;
  entry_date: string;
  hours: number;
  account_id: string;
  account_name: string;
  activity_code: string;
  activity_name: string;
  activity_category: string;
  notes: string;
  billable: boolean;
  status: string;
}

export interface StaffUtilizationEntry {
  user_id: string;
  name: string;
  billable_hours: number;
  non_billable_hours: number;
  pto_hours: number;
  total_hours: number;
  utilization_pct: number;
}

// ─── My Accounts (Account Manager dashboard; formerly "My Book") ────────────
// Surface renamed v1.5.63; internal MyBook* type names kept (spec-frozen).
// Frozen contract — docs/specs/2026-09-10-my-book-dashboard-spec.md. The payload
// ships raw project rows on purpose; every count the UI shows is derived client
// side in client/src/lib/myBookDerive.ts.

export interface MyBookProject {
  project_id: string;
  title: string;
  period_display: string | null;
  status: string; // open | in_progress | on_hold
  due_date: string | null; // YYYY-MM-DD
  overdue: boolean;
  last_activity_at: string | null;
  days_idle: number | null; // null when last_activity_at is null
  stalled: boolean;
  current_step_title: string | null;
  current_assignee_id: string | null;
  current_assignee_ids?: string[];
  current_assignee_name: string | null;
}

export interface MyBookBooks {
  sources_total: number;
  sources_by_status: { ok: number; attention: number; overdue: number };
  worst_status: "ok" | "attention" | "overdue" | null; // null = no sources
  min_through_date: string | null;
  latest_closed_bookkeeping: { title: string; period_display: string | null } | null;
}

export interface MyBookRelationship {
  last_touch: { at: string; kind: "email" | "sms" | "meeting" } | null;
  waiting_on_client: { count: number; oldest_follow_up_at: string | null };
}

/**
 * Unread rollup across the account's mapped shared mailbox(es) (v1.5.65). Null/absent =
 * no mapped mailbox OR nothing unread — either way the card hides the bubble.
 */
export interface MyBookSharedInbox {
  unread_count: number;
  oldest_unread_at: string | null;
}

export interface MyBookAccount {
  account_id: string;
  account_name: string;
  entity_type: string | null;
  projects: MyBookProject[];
  books: MyBookBooks;
  relationship: MyBookRelationship;
  /** Optional so a cached pre-v1.5.65 payload still type-checks (bubble simply hidden). */
  shared_inbox?: MyBookSharedInbox | null;
}

/**
 * The firm-tuned display banding (v1.5.64, my_accounts_settings). Optional on both dashboard
 * responses so a cached pre-v1.5.64 payload still type-checks; the client falls back to the
 * myBookDerive.ts constants when absent. Always the FIRM values — never affected by ?days=.
 */
export interface MyAccountsDisplayThresholds {
  stall_severe_days: number;
  going_quiet_days: number;
  waiting_on_client_aging_days: number;
  /** Unread-bubble age cuts (v1.5.65). Optional: a cached pre-v1.5.65 payload lacks them. */
  inbox_unread_amber_days?: number;
  inbox_unread_red_days?: number;
}

/** The firm-tunable My Accounts thresholds, as the admin settings endpoints speak them. */
export interface MyAccountsSettings {
  stall_threshold_days: number;
  stall_severe_days: number;
  going_quiet_days: number;
  waiting_on_client_aging_days: number;
  inbox_unread_amber_days: number;
  inbox_unread_red_days: number;
}

export interface MyBookResponse {
  manager_user_id: string;
  threshold_days: number;
  display_thresholds?: MyAccountsDisplayThresholds;
  /**
   * Signals that failed to load and are showing their fallback. Today the only member is
   * `"last_activity"`, whose fallback marks EVERY project stalled — indistinguishable from a
   * genuinely neglected book unless the page says so. Optional so a cached pre-v1.5.59
   * payload still type-checks.
   */
  signals_degraded?: string[];
  accounts: MyBookAccount[];
}

export interface FirmStalledGroup {
  account_id: string;
  account_name: string;
  account_manager: { user_id: string; name: string } | null; // null = no AM designated
  projects: MyBookProject[]; // stalled only
}

export interface FirmStalledResponse {
  threshold_days: number;
  display_thresholds?: MyAccountsDisplayThresholds;
  groups: FirmStalledGroup[];
}

// ---- Portal activity (Portal Responses page, account Portal tab) ----
/** Firm-wide filed stamp (migration 116). null = still open. */
export interface PortalActivityFiled {
  at: string;
  by_user_id: string | null;
  by_name: string | null;
  note: string | null;
}

/**
 * One row of GET /api/portal-activity. `upload` = client file on a task, `comment` = client
 * comment on a task, `document` = file sent through a public document-request link (only
 * returned when the caller passes ?include=documents; it has no project/task).
 */
export interface PortalActivityItem {
  id: string;
  type: "upload" | "comment" | "document";
  account_id: string;
  account_name: string;
  project_id: string | null;
  project_title: string | null;
  project_type?: string | null;
  project_tax_year?: string | number | null;
  task_id: string | null;
  task_title: string | null;
  /** document items only: the document request's title */
  request_title?: string | null;
  /** document items only: current client_documents.folder_path */
  folder_path?: string | null;
  name: string;
  file_path?: string;
  file_type?: string;
  file_size?: number;
  created_at: string;
  /** per-user read receipt (migration 099); always true for document items */
  read?: boolean;
  /** firm-wide filed stamp (migration 116); always null for comments */
  filed?: PortalActivityFiled | null;
}

export const api = {
  async getAccounts(includeInactive: boolean = false): Promise<Account[]> {
    const headers = await getAuthHeaders();
    const url = includeInactive ? "/api/accounts?includeInactive=true" : "/api/accounts";
    const response = await fetch(url, { headers });
    if (!response.ok) throw new Error("Failed to fetch accounts");
    return response.json();
  },

  async getAccount(id: string): Promise<Account> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/accounts/${id}`, { headers });
    if (!response.ok) throw new Error("Failed to fetch account");
    return response.json();
  },

  async createAccount(data: { account_name: string; smartsuite_record_id?: string }): Promise<Account> {
    const headers = await getAuthHeaders();
    const response = await fetch("/api/accounts", {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error(await errorMessage(response, "Failed to create client"));
    return response.json();
  },

  // sets the top-level account fields the accounts page owns (active flag, name, smartsuite id)
  // distinct from updateAccountAbout which PATCHes the /about profile route
  async updateAccount(id: string, data: Partial<{ is_active: boolean; account_name: string; smartsuite_record_id: string | null }>): Promise<Account> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/accounts/${id}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error(await errorMessage(response, "Failed to update client"));
    return response.json();
  },

  async pauseRecurringProjects(accountId: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/accounts/${accountId}/pause-recurring-projects`, {
      method: "POST",
      headers,
    });
    if (!response.ok) throw new Error(await errorMessage(response, "Failed to pause recurring projects"));
  },

  async closeOpenProjects(accountId: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/accounts/${accountId}/close-open-projects`, {
      method: "POST",
      headers,
    });
    if (!response.ok) throw new Error(await errorMessage(response, "Failed to close open projects"));
  },

  async updateAccountAbout(id: string, data: Partial<{
    account_name: string;
    entity_type: string;
    ein: string;
    last_4_ssn: string;
    address_1: string;
    city: string;
    state: string;
    zip: string;
    website: string;
    formation_date: string;
    s_election_date: string;
    cs_client_id: string;
    timezone: string;
  }>): Promise<Account> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/accounts/${id}/about`, {
      method: "PATCH",
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error("Failed to update account");
    return response.json();
  },

  async getAccountNotes(accountId: string): Promise<ClientNote[]> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/accounts/${accountId}/notes`, { headers });
    if (!response.ok) throw new Error("Failed to fetch client notes");
    return response.json();
  },

  async createAccountNote(accountId: string, payload: { title?: string; plain_text?: string; is_pinned?: boolean }): Promise<ClientNote> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/accounts/${accountId}/notes`, { method: "POST", headers, body: JSON.stringify(payload) });
    if (!response.ok) throw new Error("Failed to create client note");
    return response.json();
  },

  async updateAccountNote(accountId: string, noteId: string, payload: { title?: string; plain_text?: string; is_pinned?: boolean }): Promise<ClientNote> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/accounts/${accountId}/notes/${noteId}`, { method: "PATCH", headers, body: JSON.stringify(payload) });
    if (!response.ok) throw new Error("Failed to update client note");
    return response.json();
  },

  async deleteAccountNote(accountId: string, noteId: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/accounts/${accountId}/notes/${noteId}`, { method: "DELETE", headers });
    if (!response.ok) throw new Error("Failed to delete client note");
  },

  async getAccountRelationships(accountId: string): Promise<AccountRelationship[]> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/accounts/${accountId}/relationships`, { headers });
    if (!response.ok) throw new Error("Failed to fetch account relationships");
    return response.json();
  },

  async createAccountRelationship(accountId: string, data: { otherAccountId: string; label: string }): Promise<AccountRelationship> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/accounts/${accountId}/relationships`, {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error("Failed to create account relationship");
    return response.json();
  },

  async deleteAccountRelationship(accountId: string, relId: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/accounts/${accountId}/relationships/${relId}`, {
      method: "DELETE",
      headers,
    });
    if (!response.ok) throw new Error("Failed to delete account relationship");
  },

  async getAccountResources(accountId: string): Promise<AccountResource[]> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/accounts/${accountId}/resources`, { headers });
    if (!response.ok) throw new Error("Failed to fetch account resources");
    return response.json();
  },

  async createAccountResource(accountId: string, data: { label: string; url: string }): Promise<AccountResource> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/accounts/${accountId}/resources`, {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error("Failed to create account resource");
    return response.json();
  },

  async updateAccountResource(accountId: string, resId: string, data: { label?: string; url?: string }): Promise<AccountResource> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/accounts/${accountId}/resources/${resId}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error("Failed to update account resource");
    return response.json();
  },

  async deleteAccountResource(accountId: string, resId: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/accounts/${accountId}/resources/${resId}`, {
      method: "DELETE",
      headers,
    });
    if (!response.ok) throw new Error("Failed to delete account resource");
  },

  async getAccountAssignees(accountId: string): Promise<AccountAssignee[]> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/accounts/${accountId}/assignees`, { headers });
    if (!response.ok) throw new Error("Failed to fetch account assignees");
    return response.json();
  },

  async getAllAccountMembers(): Promise<{ account_id: string; user_id: string; workflow_role?: string | null }[]> {
    const headers = await getAuthHeaders();
    const response = await fetch("/api/accounts/members", { headers });
    if (!response.ok) throw new Error("Failed to fetch account members");
    return response.json();
  },

  async addAccountAssignee(accountId: string, data: { userId: string; role?: string }): Promise<AccountAssignee> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/accounts/${accountId}/assignees`, {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });
    // Surface the server's own text: these mutations are manager/admin only, so a
    // staff caller gets a 403 whose reason is worth reading in the error toast.
    if (!response.ok) throw new Error(await errorMessage(response, "Failed to add account assignee"));
    return response.json();
  },

  async removeAccountAssignee(accountId: string, userId: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/accounts/${accountId}/assignees/${userId}`, {
      method: "DELETE",
      headers,
    });
    if (!response.ok) throw new Error(await errorMessage(response, "Failed to remove account assignee"));
  },

  async getAccountRecentActivities(accountId: string): Promise<AccountActivity[]> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/accounts/${accountId}/recent-activities`, { headers });
    if (!response.ok) throw new Error("Failed to fetch recent activities");
    return response.json();
  },

  async getAccountTasks(accountId: string, includeCompleted = false): Promise<ProjectTask[]> {
    const headers = await getAuthHeaders();
    const params = new URLSearchParams({ account_id: accountId });
    if (!includeCompleted) params.append("status", "open");
    const response = await fetch(`/api/project-tasks?${params}`, { headers });
    if (!response.ok) throw new Error("Failed to fetch account tasks");
    const raw: TaskWithContext[] = await response.json();
    return raw.map(taskWithContextToProjectTask);
  },

  async getActivityCodes(): Promise<ActivityCode[]> {
    const headers = await getAuthHeaders();
    const response = await fetch("/api/activity-codes", { headers });
    if (!response.ok) throw new Error("Failed to fetch activity codes");
    return response.json();
  },

  async getPayPeriods(): Promise<PayPeriod[]> {
    const headers = await getAuthHeaders();
    const response = await fetch("/api/pay-periods", { headers });
    if (!response.ok) throw new Error("Failed to fetch pay periods");
    return response.json();
  },

  async getProjects(params?: { accountId?: string; status?: string; search?: string; includeAll?: boolean }): Promise<Project[]> {
    const headers = await getAuthHeaders();
    const searchParams = new URLSearchParams();
    if (params?.accountId) searchParams.set("account_id", params.accountId);
    if (params?.status) searchParams.set("status", params.status);
    if (params?.search) searchParams.set("search", params.search);
    if (params?.includeAll) searchParams.set("include_all", "true");
    const url = `/api/projects${searchParams.toString() ? `?${searchParams}` : ""}`;
    const response = await fetch(url, { headers });
    if (!response.ok) throw new Error("Failed to fetch projects");
    return response.json();
  },

  // id + status + title only, for pages that just need to know which projects are closed (ENG-131)
  async getProjectStatuses(): Promise<{ id: string; status: string; title: string }[]> {
    const headers = await getAuthHeaders();
    const response = await fetch("/api/projects/statuses", { headers });
    if (!response.ok) throw new Error("Failed to fetch project statuses");
    return response.json();
  },

  async getProject(id: string): Promise<Project> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/projects/${id}`, { headers });
    if (!response.ok) throw new Error("Failed to fetch project");
    return response.json();
  },

  async createProject(project: {
    title: string;
    account_id: string;
    description?: string | null;
    start_date?: string | null;
    due_date?: string | null;
    internal_due_date?: string | null;
    status?: string;
    billing_rate?: number | null;
    template_id?: string | null;
    recurring_pattern?: string | null;
    accounting_period_date?: string | null;
    computed_accounting_period?: string | null;
    project_type?: string | null;
    tax_year?: number | null;
    period_label?: string | null;
  }): Promise<Project> {
    const headers = await getAuthHeaders();
    const response = await fetch("/api/projects", {
      method: "POST",
      headers,
      body: JSON.stringify(project),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to create project");
    }
    return response.json();
  },

  async bulkCreateProjects(payload: Record<string, unknown> & { account_ids: string[]; title: string }): Promise<{ created: number; skipped: string[]; failed: { account_id: string; error: string }[] }> {
    return fetchWithAuth("/api/projects/bulk", { method: "POST", body: JSON.stringify(payload) });
  },

  async updateProject(id: string, updates: Partial<Project>): Promise<Project> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/projects/${id}`, {
      method: "PUT",
      headers,
      body: JSON.stringify(updates),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to update project");
    }
    return response.json();
  },

  async deleteProject(id: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/projects/${id}`, {
      method: "DELETE",
      headers,
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to delete project");
    }
  },

  async markProjectReadyForReview(id: string): Promise<void> {
    return fetchWithAuth(`/api/projects/${id}/mark-ready-for-review`, { method: "POST" });
  },

  async scheduleClientReminder(projectId: string, contacts: string[], scheduledFor: string): Promise<{ id: string }> {
    return fetchWithAuth(`/api/projects/${projectId}/schedule-client-reminder`, {
      method: "POST",
      body: JSON.stringify({ contacts, scheduled_for: scheduledFor }),
    });
  },

  async getScheduledReminders(projectId: string): Promise<{ id: string; scheduled_for: string; contacts: string[]; status: string }[]> {
    return fetchWithAuth(`/api/projects/${projectId}/scheduled-reminders`);
  },

  async cancelScheduledReminder(reminderId: string): Promise<void> {
    return fetchWithAuth(`/api/scheduled-client-reminders/${reminderId}`, { method: "DELETE" });
  },

  // Per-section client reminders (migration 058)
  async getSectionReminder(sectionId: string): Promise<SectionReminderConfig | null> {
    return fetchWithAuth(`/api/project-sections/${sectionId}/reminder`);
  },
  async upsertSectionReminder(sectionId: string, config: { days: number; is_active: boolean; contacts: string[]; schedule_type?: string; monthly_day?: number | null; trigger_section_id?: string | null }): Promise<SectionReminderConfig> {
    return fetchWithAuth(`/api/project-sections/${sectionId}/reminder`, {
      method: "PUT",
      body: JSON.stringify(config),
    });
  },
  async sendSectionReminderNow(sectionId: string): Promise<{ sent: boolean; taskCount: number }> {
    return fetchWithAuth(`/api/project-sections/${sectionId}/reminder/send-now`, { method: "POST" });
  },

  async runRecurringSpawn(): Promise<{ spawned: number; errors: string[] }> {
    return fetchWithAuth("/api/projects/spawn-recurring", { method: "POST" });
  },

  async spawnProjectNow(projectId: string): Promise<{ ok: boolean }> {
    return fetchWithAuth(`/api/projects/${projectId}/spawn`, { method: "POST" });
  },

  async getProjectUsers(projectId: string): Promise<ProjectUser[]> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/projects/${projectId}/users`, { headers });
    if (!response.ok) throw new Error("Failed to fetch project users");
    return response.json();
  },

  async addProjectUser(projectId: string, userId: string, role: string): Promise<ProjectUser> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/projects/${projectId}/users`, {
      method: "POST",
      headers,
      body: JSON.stringify({ user_id: userId, role }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to add user to project");
    }
    return response.json();
  },

  async removeProjectUser(projectId: string, userId: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/projects/${projectId}/users/${userId}`, {
      method: "DELETE",
      headers,
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to remove user from project");
    }
  },

  async getProjectSections(projectId: string): Promise<TaskSection[]> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/projects/${projectId}/sections`, { headers });
    if (!response.ok) throw new Error("Failed to fetch project sections");
    return response.json();
  },

  async createProjectSection(projectId: string, section: TaskSectionInput): Promise<TaskSection> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/projects/${projectId}/sections`, {
      method: "POST",
      headers,
      body: JSON.stringify(section),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to create section");
    }
    return response.json();
  },

  async getProjectTasks(projectId: string): Promise<ProjectTask[]> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/projects/${projectId}/tasks`, { headers });
    if (!response.ok) throw new Error("Failed to fetch project tasks");
    return response.json();
  },

  async completeTask(taskId: string): Promise<ProjectTask> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/project-tasks/${taskId}/complete`, { method: "POST", headers });
    if (!response.ok) throw new Error("Failed to complete task");
    return response.json();
  },

  async uncompleteTask(taskId: string): Promise<ProjectTask> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/project-tasks/${taskId}/uncomplete`, { method: "POST", headers });
    if (!response.ok) throw new Error("Failed to uncomplete task");
    return response.json();
  },

  async sendTaskToClient(taskId: string): Promise<ProjectTask> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/project-tasks/${taskId}/send-to-client`, { method: "POST", headers });
    if (!response.ok) throw new Error("Failed to send task to client");
    return response.json();
  },

  async getProjectMessages(projectId: string): Promise<ProjectMessage[]> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/projects/${projectId}/messages`, { headers });
    if (!response.ok) throw new Error("Failed to fetch project messages");
    return response.json();
  },

  async postProjectMessage(projectId: string, body: string, isClientVisible: boolean): Promise<ProjectMessage> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/projects/${projectId}/messages`, {
      method: "POST",
      headers,
      body: JSON.stringify({ body, is_client_visible: isClientVisible }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error((error as any).message || "Failed to post message");
    }
    return response.json();
  },

  // edit team comments
  async updateProjectMessage(messageId: string, body: string): Promise<ProjectMessage> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/project-messages/${messageId}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify({ body }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error((error as any).message || "Failed to edit message");
    }
    return response.json();
  },

  // portal activity count, requires migration 099
  async markPortalActivityRead(
    items: { type: "upload" | "comment"; id: string; account_id?: string }[]
  ): Promise<{ marked: number }> {
    const headers = await getAuthHeaders();
    const response = await fetch("/api/portal-activity/read", {
      method: "POST",
      headers,
      body: JSON.stringify({ items }),
    });
    if (!response.ok) throw new Error("Failed to mark portal activity read");
    return response.json();
  },

  async markAccountPortalActivityRead(accountId: string): Promise<{ marked: number }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/accounts/${accountId}/portal-activity/read-all`, {
      method: "POST",
      headers,
    });
    if (!response.ok) throw new Error("Failed to mark portal activity read");
    return response.json();
  },

  // Firm-wide "filed" stamp on incoming client documents (migration 116). Unlike read
  // receipts, one filing counts for everyone; re-filing an already-filed item is a no-op.
  async markPortalActivityFiled(
    items: { type: "upload" | "document"; id: string; account_id?: string | null }[],
    note?: string
  ): Promise<{ filed: number }> {
    const headers = await getAuthHeaders();
    const response = await fetch("/api/portal-activity/file", {
      method: "POST",
      headers,
      body: JSON.stringify({ items, note: note ?? null }),
    });
    if (!response.ok) throw new Error("Failed to mark as filed");
    return response.json();
  },

  async unfilePortalActivity(type: "upload" | "document", id: string): Promise<{ removed: number }> {
    const headers = await getAuthHeaders();
    const response = await fetch("/api/portal-activity/unfile", {
      method: "POST",
      headers,
      body: JSON.stringify({ type, id }),
    });
    if (!response.ok) throw new Error("Failed to unfile");
    return response.json();
  },

  async getClientTopics(accountId: string): Promise<ClientTopic[]> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/accounts/${accountId}/client-topics`, { headers });
    if (!response.ok) throw new Error("Failed to fetch client topics");
    return response.json();
  },

  async createClientTopic(accountId: string, name: string): Promise<ClientTopic> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/accounts/${accountId}/client-topics`, {
      method: "POST",
      headers,
      body: JSON.stringify({ name }),
    });
    if (!response.ok) throw new Error("Failed to create topic");
    return response.json();
  },

  async getClientTopicMessages(topicId: string): Promise<ClientTopicMessage[]> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/client-topics/${topicId}/messages`, { headers });
    if (!response.ok) throw new Error("Failed to fetch messages");
    return response.json();
  },

  async postClientTopicMessage(topicId: string, body: string): Promise<ClientTopicMessage> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/client-topics/${topicId}/messages`, {
      method: "POST",
      headers,
      body: JSON.stringify({ body }),
    });
    if (!response.ok) throw new Error("Failed to post message");
    return response.json();
  },

  // edit client message
  async updateClientTopicMessage(topicId: string, messageId: string, body: string): Promise<ClientTopicMessage> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/client-topics/${topicId}/messages/${messageId}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify({ body }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error((error as any).message || "Failed to edit message");
    }
    return response.json();
  },

  async createProjectTask(projectId: string, task: {
    title: string;
    description?: string | null;
    section_id?: string | null;
    due_date?: string | null;
    assigned_user_id?: string | null;
    is_client_task?: boolean;
    allow_client_upload?: boolean;
    budgeted_minutes?: number | null;
    parent_task_id?: string | null;
    financial_source_id?: string | null;
  }): Promise<ProjectTask> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/projects/${projectId}/tasks`, {
      method: "POST",
      headers,
      body: JSON.stringify(task),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to create task");
    }
    return response.json();
  },

  async updateProjectTask(taskId: string, updates: {
    title?: string;
    due_date?: string | null;
    internal_due_date?: string | null;
    assigned_user_id?: string | null;
    description?: string | null;
    allow_client_upload?: boolean;
  }): Promise<ProjectTask> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/project-tasks/${taskId}`, {
      method: "PUT",
      headers,
      body: JSON.stringify(updates),
    });
    if (!response.ok) throw new Error("Failed to update task");
    return response.json();
  },

  async deleteProjectTask(taskId: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/project-tasks/${taskId}`, {
      method: "DELETE",
      headers,
    });
    if (!response.ok) throw new Error("Failed to delete task");
  },

  async getTaskAssignees(taskId: string): Promise<TaskAssignee[]> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/project-tasks/${taskId}/assignees`, { headers });
    if (!response.ok) throw new Error("Failed to fetch assignees");
    return response.json();
  },

  async addTaskAssignee(taskId: string, userId: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/project-tasks/${taskId}/assignees`, {
      method: "POST",
      headers,
      body: JSON.stringify({ user_id: userId }),
    });
    if (!response.ok) throw new Error("Failed to add assignee");
  },

  async removeTaskAssignee(taskId: string, userId: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/project-tasks/${taskId}/assignees/${userId}`, {
      method: "DELETE",
      headers,
    });
    if (!response.ok) throw new Error("Failed to remove assignee");
  },

  async getUsers(opts?: { assignable?: boolean }): Promise<User[]> {
    const headers = await getAuthHeaders();
    // assignable=1 also excludes contractors (for email/task claim pickers).
    const qs = opts?.assignable ? "?assignable=1" : "";
    const response = await fetch(`/api/users${qs}`, { headers });
    if (!response.ok) throw new Error("Failed to fetch users");
    return response.json();
  },

  async inviteTeamMember(data: { email: string; name: string; role: "staff" | "contractor" | "manager" | "admin" }): Promise<{ message: string; userId: string }> {
    return fetchWithAuth("/api/admin/invite", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
  },

  async getAdminUsers(): Promise<AdminUser[]> {
    return fetchWithAuth<AdminUser[]>("/api/admin/users");
  },

  async updateUserRole(userId: string, role: "staff" | "manager" | "admin" | "contractor" | "client"): Promise<void> {
    await fetchWithAuth(`/api/admin/users/${userId}/role`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ role }),
    });
  },

  async updateUserStatus(userId: string, is_active: boolean): Promise<void> {
    await fetchWithAuth(`/api/admin/users/${userId}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ is_active }),
    });
  },

  async deleteUser(userId: string): Promise<void> {
    await fetchWithAuth(`/api/admin/users/${userId}`, { method: "DELETE" });
  },

  async updateUser(userId: string, updates: { name?: string; email?: string; role?: string; is_active?: boolean; calendar_email?: string | null }): Promise<void> {
    await fetchWithAuth(`/api/admin/users/${userId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updates),
    });
  },

  async resendInvite(userId: string): Promise<void> {
    await fetchWithAuth(`/api/admin/users/${userId}/resend-invite`, { method: "POST" });
  },

  async getCurrentPayPeriod(): Promise<PayPeriod | null> {
    const headers = await getAuthHeaders();
    const response = await fetch("/api/pay-periods/current", { headers });
    if (!response.ok) throw new Error("Failed to fetch current pay period");
    return response.json();
  },

  async getTimeEntries(params?: { startDate?: string; endDate?: string; payPeriodId?: string }): Promise<TimeEntry[]> {
    const headers = await getAuthHeaders();
    const searchParams = new URLSearchParams();
    if (params?.startDate) searchParams.set("startDate", params.startDate);
    if (params?.endDate) searchParams.set("endDate", params.endDate);
    if (params?.payPeriodId) searchParams.set("payPeriodId", params.payPeriodId);
    
    const url = `/api/time-entries${searchParams.toString() ? `?${searchParams}` : ""}`;
    const response = await fetch(url, { headers });
    if (!response.ok) throw new Error("Failed to fetch time entries");
    return response.json();
  },

  async createTimeEntry(entry: {
    entry_date: string;
    minutes: number;
    start_time?: string | null;
    end_time?: string | null;
    account_id: string | null;
    project_id?: string | null;
    activity_code: string;
    notes?: string;
    billable: boolean;
  }): Promise<TimeEntry> {
    const headers = await getAuthHeaders();
    const response = await fetch("/api/time-entries", {
      method: "POST",
      headers,
      body: JSON.stringify(entry),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to create time entry");
    }
    return response.json();
  },

  async updateTimeEntry(id: string, updates: Partial<{
    entry_date: string;
    minutes: number;
    start_time: string | null;
    end_time: string | null;
    account_id: string | null;
    project_id: string | null;
    activity_code: string;
    notes: string;
    billable: boolean;
  }>): Promise<TimeEntry> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/time-entries/${id}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify(updates),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to update time entry");
    }
    return response.json();
  },

  async deleteTimeEntry(id: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/time-entries/${id}`, {
      method: "DELETE",
      headers,
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to delete time entry");
    }
  },

  async submitTimeEntries(entryIds: string[]): Promise<{ updated: number }> {
    const headers = await getAuthHeaders();
    const response = await fetch("/api/time-entries/submit", {
      method: "POST",
      headers,
      body: JSON.stringify({ entryIds }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to submit time entries");
    }
    return response.json();
  },

  async getPTOBalance(): Promise<{
    balance: number;
    accrued: number;
    used: number;
    pending: number;
  }> {
    const headers = await getAuthHeaders();
    const response = await fetch("/api/pto/balance", { headers });
    if (!response.ok) throw new Error("Failed to fetch PTO balance");
    return response.json();
  },

  async getReviewEntries(): Promise<TimeEntry[]> {
    const headers = await getAuthHeaders();
    const response = await fetch("/api/review/entries", { headers });
    if (!response.ok) throw new Error("Failed to fetch review entries");
    return response.json();
  },

  async approveTimeEntries(entryIds: string[]): Promise<{ updated: number }> {
    const headers = await getAuthHeaders();
    const response = await fetch("/api/review/approve", {
      method: "POST",
      headers,
      body: JSON.stringify({ entryIds }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to approve entries");
    }
    return response.json();
  },

  async rejectTimeEntries(entryIds: string[], note: string): Promise<{ updated: number }> {
    const headers = await getAuthHeaders();
    const response = await fetch("/api/review/reject", {
      method: "POST",
      headers,
      body: JSON.stringify({ entryIds, note }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to reject entries");
    }
    return response.json();
  },

  async getUtilizationReport(startDate: string, endDate: string): Promise<{
    total: number;
    billable: number;
    nonBillable: number;
    pto: number;
    utilizationRate: number;
  }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/reports/utilization?startDate=${startDate}&endDate=${endDate}`, { headers });
    if (!response.ok) throw new Error("Failed to fetch utilization report");
    return response.json();
  },

  async getTeamTimeEntries(params: { startDate: string; endDate: string; userId?: string }): Promise<TeamTimeEntry[]> {
    const headers = await getAuthHeaders();
    const q = new URLSearchParams({ startDate: params.startDate, endDate: params.endDate });
    if (params.userId) q.set("userId", params.userId);
    const response = await fetch(`/api/reports/team-time-entries?${q}`, { headers });
    if (!response.ok) throw new Error("Failed to fetch team time entries");
    return response.json();
  },

  async getStaffUtilization(startDate: string, endDate: string): Promise<StaffUtilizationEntry[]> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/reports/staff-utilization?startDate=${startDate}&endDate=${endDate}`, { headers });
    if (!response.ok) throw new Error("Failed to fetch staff utilization");
    return response.json();
  },

  async getUserRoles(): Promise<{ userId: string; userName: string; role: string }[]> {
    const headers = await getAuthHeaders();
    const response = await fetch("/api/reports/user-roles", { headers });
    if (!response.ok) throw new Error("Failed to fetch user roles");
    return response.json();
  },

  async createPayPeriod(data: { start_date: string; end_date: string; pay_date: string }): Promise<PayPeriod> {
    const headers = await getAuthHeaders();
    const response = await fetch("/api/pay-periods/generate", {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to create pay period");
    }
    return response.json();
  },

  async closePayPeriod(id: string): Promise<PayPeriod> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/pay-periods/${id}/close`, {
      method: "POST",
      headers,
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to close pay period");
    }
    return response.json();
  },

  async reopenPayPeriod(id: string): Promise<PayPeriod> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/pay-periods/${id}/reopen`, {
      method: "POST",
      headers,
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to reopen pay period");
    }
    return response.json();
  },

  // ============= INVOICES =============
  async getInvoices(params?: { status?: string; accountId?: string }): Promise<Invoice[]> {
    const headers = await getAuthHeaders();
    const searchParams = new URLSearchParams();
    if (params?.status && params.status !== "all") searchParams.set("status", params.status);
    if (params?.accountId) searchParams.set("accountId", params.accountId);
    
    const url = `/api/invoices${searchParams.toString() ? `?${searchParams}` : ""}`;
    const response = await fetch(url, { headers });
    if (!response.ok) throw new Error("Failed to fetch invoices");
    return response.json();
  },

  async getInvoice(id: string): Promise<Invoice & { lines: InvoiceLine[] }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/invoices/${id}`, { headers });
    if (!response.ok) throw new Error("Failed to fetch invoice");
    return response.json();
  },

  async createInvoice(data: {
    account_id: string;
    invoice_number?: string;
    invoice_date?: string;
    due_date?: string;
    notes?: string;
  }): Promise<Invoice> {
    const headers = await getAuthHeaders();
    const response = await fetch("/api/invoices", {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to create invoice");
    }
    return response.json();
  },

  async updateInvoice(id: string, updates: Partial<{
    account_id: string;
    invoice_number: string;
    invoice_date: string;
    due_date: string;
    status: string;
    notes: string;
    tax: number;
    paid_date: string;
    payment_method: string;
    payment_reference: string;
  }>): Promise<Invoice> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/invoices/${id}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify(updates),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to update invoice");
    }
    return response.json();
  },

  async deleteInvoice(id: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/invoices/${id}`, {
      method: "DELETE",
      headers,
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to delete invoice");
    }
  },

  async addInvoiceLine(invoiceId: string, line: {
    description: string;
    quantity?: number;
    rate?: number;
    linked_time_entry_id?: string;
  }): Promise<InvoiceLine> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/invoices/${invoiceId}/lines`, {
      method: "POST",
      headers,
      body: JSON.stringify(line),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to add invoice line");
    }
    return response.json();
  },

  async updateInvoiceLine(id: string, updates: {
    description?: string;
    quantity?: number;
    rate?: number;
  }): Promise<InvoiceLine> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/invoice-lines/${id}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify(updates),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to update invoice line");
    }
    return response.json();
  },

  async deleteInvoiceLine(id: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/invoice-lines/${id}`, {
      method: "DELETE",
      headers,
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to delete invoice line");
    }
  },

  async createInvoiceFromTimeEntries(data: {
    account_id: string;
    date_from: string;
    date_to: string;
    tax_rate?: number;
    hourly_rate?: number;
  }): Promise<Invoice> {
    const headers = await getAuthHeaders();
    const response = await fetch("/api/invoices/from-time-entries", {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to create invoice from time entries");
    }
    return response.json();
  },

  // Contacts
  async getContacts(params?: { accountId?: string; status?: string; search?: string }): Promise<Contact[]> {
    const headers = await getAuthHeaders();
    const searchParams = new URLSearchParams();
    if (params?.accountId) searchParams.set("account_id", params.accountId);
    if (params?.status) searchParams.set("status", params.status);
    if (params?.search) searchParams.set("search", params.search);
    const url = `/api/contacts${searchParams.toString() ? `?${searchParams}` : ""}`;
    const response = await fetch(url, { headers });
    if (!response.ok) throw new Error("Failed to fetch contacts");
    return response.json();
  },

  async getContact(id: string): Promise<Contact> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/contacts/${id}`, { headers });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to fetch contact");
    }
    return response.json();
  },

  async createContact(data: ContactInput): Promise<Contact> {
    const headers = await getAuthHeaders();
    const response = await fetch("/api/contacts", {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to create contact");
    }
    return response.json();
  },

  async updateContact(id: string, data: Partial<ContactInput>): Promise<Contact> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/contacts/${id}`, {
      method: "PUT",
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to update contact");
    }
    return response.json();
  },

  async patchContactSmsOk(id: string, sms_ok: boolean): Promise<Contact> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/contacts/${id}/sms-ok`, {
      method: "PATCH",
      headers,
      body: JSON.stringify({ sms_ok }),
    });
    if (!response.ok) throw new Error("Failed to update SMS opt-in");
    return response.json();
  },

  async getSmsLog(accountId: string): Promise<SmsLogEntry[]> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/accounts/${accountId}/sms-log`, { headers });
    if (!response.ok) throw new Error("Failed to fetch SMS log");
    return response.json();
  },

  async getSmsConversations(): Promise<SmsConversation[]> {
    const headers = await getAuthHeaders();
    const response = await fetch("/api/sms/conversations", { headers });
    if (!response.ok) throw new Error("Failed to fetch SMS conversations");
    return response.json();
  },

  async markSmsConversationRead(accountId: string): Promise<void> {
    const headers = await getAuthHeaders();
    await fetch(`/api/sms/conversations/${accountId}/mark-read`, { method: "POST", headers });
  },

  async sendSmsFromProfile(params: {
    to: string;
    body: string;
    account_id: string;
    contact_id?: string;
  }): Promise<{ sent: boolean; sid: string }> {
    const headers = await getAuthHeaders();
    const response = await fetch("/api/admin/sms/send", {
      method: "POST",
      headers,
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || "Failed to send SMS");
    }
    return response.json();
  },

  async deleteContact(id: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/contacts/${id}`, {
      method: "DELETE",
      headers,
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to deactivate contact");
    }
  },

  async reactivateContact(id: string): Promise<Contact> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/contacts/${id}/reactivate`, {
      method: "PUT",
      headers,
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to reactivate contact");
    }
    return response.json();
  },

  // Project Roles
  async getProjectRoles(): Promise<ProjectRole[]> {
    const headers = await getAuthHeaders();
    const response = await fetch("/api/project-roles", { headers });
    if (!response.ok) throw new Error("Failed to fetch project roles");
    return response.json();
  },

  // Project Templates
  async getProjectTemplates(filters?: { status?: string; recurring_pattern?: string }): Promise<ProjectTemplate[]> {
    const headers = await getAuthHeaders();
    const params = new URLSearchParams();
    if (filters?.status) params.set("status", filters.status);
    if (filters?.recurring_pattern) params.set("recurring_pattern", filters.recurring_pattern);
    const query = params.toString();
    const response = await fetch(`/api/project-templates${query ? `?${query}` : ""}`, { headers });
    if (!response.ok) throw new Error("Failed to fetch project templates");
    return response.json();
  },

  async getProjectTemplate(id: string): Promise<ProjectTemplate> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/project-templates/${id}`, { headers });
    if (!response.ok) throw new Error("Failed to fetch project template");
    return response.json();
  },

  async createProjectTemplate(data: ProjectTemplateInput): Promise<ProjectTemplate> {
    const headers = await getAuthHeaders();
    const response = await fetch("/api/project-templates", {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to create project template");
    }
    return response.json();
  },

  async updateProjectTemplate(id: string, data: Partial<ProjectTemplateInput>): Promise<ProjectTemplate> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/project-templates/${id}`, {
      method: "PUT",
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to update project template");
    }
    return response.json();
  },

  async deleteProjectTemplate(id: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/project-templates/${id}`, {
      method: "DELETE",
      headers,
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to delete project template");
    }
  },

  // Workflow Pipelines
  async getWorkflowPipelines(): Promise<WorkflowPipeline[]> {
    const headers = await getAuthHeaders();
    const response = await fetch("/api/workflow-pipelines", { headers });
    if (!response.ok) throw new Error("Failed to fetch workflow pipelines");
    return response.json();
  },

  async setProjectWorkflowStatus(projectId: string, statusId: string): Promise<Project> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/projects/${projectId}/workflow-status`, {
      method: "PATCH",
      headers,
      body: JSON.stringify({ status_id: statusId }),
    });
    if (!response.ok) throw new Error("Failed to update workflow status");
    return response.json();
  },

  async setAssigneeWorkflowRole(accountId: string, userId: string, workflowRole: string | null): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/accounts/${accountId}/assignees/${userId}/workflow-role`, {
      method: "PATCH",
      headers,
      body: JSON.stringify({ workflow_role: workflowRole }),
    });
    // The server maps the one-account-manager unique violation to a 409 carrying
    // "This client already has an account manager" — the hardcoded message this
    // replaced swallowed it, leaving the user with no idea why the save failed.
    if (!response.ok) throw new Error(await errorMessage(response, "Failed to update workflow role"));
  },

  // Tags — list all
  async getTags(): Promise<Tag[]> {
    const headers = await getAuthHeaders();
    const response = await fetch("/api/tags", { headers });
    if (!response.ok) throw new Error("Failed to fetch tags");
    return response.json();
  },

  // Tags — project assignment
  async getProjectTags(projectId: string): Promise<Tag[]> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/projects/${projectId}/tags`, { headers });
    if (!response.ok) throw new Error("Failed to fetch project tags");
    return response.json();
  },

  async addProjectTag(projectId: string, tagId: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/projects/${projectId}/tags`, {
      method: "POST",
      headers,
      body: JSON.stringify({ tag_id: tagId }),
    });
    if (!response.ok && response.status !== 409) throw new Error("Failed to add project tag");
  },

  async removeProjectTag(projectId: string, tagId: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/projects/${projectId}/tags/${tagId}`, {
      method: "DELETE",
      headers,
    });
    if (!response.ok) throw new Error("Failed to remove project tag");
  },

  // Tags — task assignment
  async getTaskTags(taskId: string): Promise<Tag[]> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/project-tasks/${taskId}/tags`, { headers });
    if (!response.ok) throw new Error("Failed to fetch task tags");
    return response.json();
  },

  async addTaskTag(taskId: string, tagId: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/project-tasks/${taskId}/tags`, {
      method: "POST",
      headers,
      body: JSON.stringify({ tag_id: tagId }),
    });
    if (!response.ok && response.status !== 409) throw new Error("Failed to add task tag");
  },

  async removeTaskTag(taskId: string, tagId: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/project-tasks/${taskId}/tags/${tagId}`, {
      method: "DELETE",
      headers,
    });
    if (!response.ok) throw new Error("Failed to remove task tag");
  },

  // Workflow status tag rules
  async getStatusTagRules(statusId: string): Promise<StatusTagRule[]> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/workflow-statuses/${statusId}/tag-rules`, { headers });
    if (!response.ok) throw new Error("Failed to fetch status tag rules");
    return response.json();
  },

  async addStatusTagRule(statusId: string, tagId: string, action: "add" | "remove"): Promise<StatusTagRule> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/workflow-statuses/${statusId}/tag-rules`, {
      method: "POST",
      headers,
      body: JSON.stringify({ tag_id: tagId, action }),
    });
    if (!response.ok) throw new Error("Failed to create tag rule");
    return response.json();
  },

  async deleteStatusTagRule(statusId: string, ruleId: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/workflow-statuses/${statusId}/tag-rules/${ruleId}`, {
      method: "DELETE",
      headers,
    });
    if (!response.ok) throw new Error("Failed to delete tag rule");
  },

  // Template default tags
  async getTemplateDefaultTags(templateId: string): Promise<Tag[]> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/project-templates/${templateId}/default-tags`, { headers });
    if (!response.ok) throw new Error("Failed to fetch template default tags");
    return response.json();
  },

  async addTemplateDefaultTag(templateId: string, tagId: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/project-templates/${templateId}/default-tags`, {
      method: "POST",
      headers,
      body: JSON.stringify({ tag_id: tagId }),
    });
    if (!response.ok) throw new Error("Failed to add template default tag");
  },

  async removeTemplateDefaultTag(templateId: string, tagId: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/project-templates/${templateId}/default-tags/${tagId}`, {
      method: "DELETE",
      headers,
    });
    if (!response.ok) throw new Error("Failed to remove template default tag");
  },

  // Task Sections
  async createTaskSection(templateId: string, data: TaskSectionInput): Promise<TaskSection> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/project-templates/${templateId}/sections`, {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to create task section");
    }
    return response.json();
  },

  async updateTaskSection(id: string, data: Partial<TaskSectionInput>): Promise<TaskSection> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/task-sections/${id}`, {
      method: "PUT",
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to update task section");
    }
    return response.json();
  },

  async deleteTaskSection(id: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/task-sections/${id}`, {
      method: "DELETE",
      headers,
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to delete task section");
    }
  },

  // Task Templates
  async createTaskTemplate(sectionId: string, data: TaskTemplateInput): Promise<TaskTemplate> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/task-sections/${sectionId}/tasks`, {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to create task template");
    }
    return response.json();
  },

  async updateTaskTemplate(id: string, data: Partial<TaskTemplateInput>): Promise<TaskTemplate> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/task-templates/${id}`, {
      method: "PUT",
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to update task template");
    }
    return response.json();
  },

  async deleteTaskTemplate(id: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetch(`/api/task-templates/${id}`, {
      method: "DELETE",
      headers,
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to delete task template");
    }
  },

  // ─── Agreement Templates ──────────────────────────────────────────────────
  getAgreementTemplates: () => getAgreementTemplates(),
  getAgreementTemplate: (id: string) => getAgreementTemplate(id),
  createAgreementTemplate: (data: AgreementTemplateInput) => createAgreementTemplate(data),
  updateAgreementTemplate: (id: string, data: Partial<AgreementTemplateInput>) => updateAgreementTemplate(id, data),
  deleteAgreementTemplate: (id: string) => deleteAgreementTemplate(id),

  // ─── Signature Requests ───────────────────────────────────────────────────
  getSignatureRequests: (statusFilter?: string) => getSignatureRequests(statusFilter),
  getAccountSignatureRequests: (accountId: string) => getAccountSignatureRequests(accountId),
  sendForSignature: (accountId: string, data: Parameters<typeof sendForSignature>[1]) => sendForSignature(accountId, data),
  sendSignatureReminder: (requestId: string) => sendSignatureReminder(requestId),
  voidSignatureRequest: (requestId: string) => voidSignatureRequest(requestId),
  syncSignatureRequest: (requestId: string) => syncSignatureRequest(requestId),

  // ─── My Accounts (formerly My Book) ───────────────────────────────────────
  /**
   * The signed-in user's book of clients (one health card each).
   * `days` overrides the 7-day stall threshold; `userId` is honored only for
   * manager/admin callers — the server forces it to the caller otherwise.
   */
  async getMyBook(opts?: { days?: number; userId?: string }): Promise<MyBookResponse> {
    const headers = await getAuthHeaders();
    const params = new URLSearchParams();
    if (opts?.days) params.set("days", String(opts.days));
    if (opts?.userId) params.set("userId", opts.userId);
    const qs = params.toString();
    const response = await fetch(`/api/my-accounts${qs ? `?${qs}` : ""}`, { headers });
    if (!response.ok) throw new Error(await errorMessage(response, "Failed to load My Accounts"));
    return response.json();
  },

  /** Firm-wide stalled work grouped by account (manager/admin only). */
  async getFirmStalled(opts?: { days?: number }): Promise<FirmStalledResponse> {
    const headers = await getAuthHeaders();
    const qs = opts?.days ? `?days=${opts.days}` : "";
    const response = await fetch(`/api/my-accounts/firm${qs}`, { headers });
    if (!response.ok) throw new Error(await errorMessage(response, "Failed to load firm stalled work"));
    return response.json();
  },
};

export interface Invoice {
  id: string;
  account_id: string;
  invoice_number: string | null;
  invoice_date: string;
  due_date: string | null;
  status: "draft" | "sent" | "paid" | "void";
  notes: string | null;
  subtotal: string | number | null;
  tax: string | number | null;
  total: string | number | null;
  paid_date: string | null;
  payment_method: string | null;
  payment_reference: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  accounts?: { id: string; account_name: string } | null;
  status_log?: Array<{ status: string; changed_at: string; changed_by_id: string; changed_by_name: string }>;
}

export interface InvoiceLine {
  id: string;
  invoice_id: string;
  description: string;
  quantity: string | number;
  rate: string | number;
  amount: string | number;
  linked_time_entry_id: string | null;
  created_at: string;
}

// ============= EMAIL INTEGRATION (Phase 11a) =============

export interface GraphEmailAddress {
  name: string;
  address: string;
}

export interface ClientEmail {
  id: string;
  subject: string | null;
  bodyPreview: string | null;
  receivedDateTime: string;
  hasAttachments: boolean;
  isRead: boolean;
  from: { emailAddress: GraphEmailAddress } | null;
  toRecipients: Array<{ emailAddress: GraphEmailAddress }>;
  ccRecipients: Array<{ emailAddress: GraphEmailAddress }>;
  mailboxAddress: string;
  /** True when the email was fetched from a shared mailbox (info@, support@, etc.) */
  isSharedMailbox?: boolean;
  /** Set by the client after fetching pinned emails for the project context */
  pinnedEmailId?: string;
  /** Outlook categories (named color tags) — two-way synced (093). */
  categories?: string[];
  /** Provenance (090): dedicated client mailbox vs surfaced by contact match on this account. */
  via?: "client-mailbox" | "contact-match";
  /** True when the source mailbox is a staff personal inbox (only ever contact-matched mail). */
  isPersonalMailbox?: boolean;
  /** Archive folder (097): sent rows show on the Emails tab with a "Sent" badge, never the Shared Inbox tab. */
  folder?: "inbox" | "sent" | string;
  /** Native Outlook follow-up flag — archived rows carry it; live-Graph rows may too. */
  flag?: { flagStatus: "notFlagged" | "flagged" | "complete" } | null;
  // Claim/pin overlays (email-surface unification) — same fields /api/inbox returns, so the
  // account tabs render identical state without extra client queries.
  taskStatus?: string;
  assignedToUserId?: string;
  assignedToName?: string;
  taskNotes?: string;
  pinned?: boolean;
  pinnedProjectId?: string;
  pinnedProjectTitle?: string;
  /** ENG-93: this personal-mailbox copy has a shared-mailbox twin (same internet_message_id). */
  hidden_by_cross_mailbox_dupe?: boolean;
  /** The id of the row rendered as the full email — set whenever hidden_by_cross_mailbox_dupe is true. */
  visible_copy_id?: string | null;
  /** ENG-98: shared rows only — who a transport-rule BCC copy was originally sent to; null = addressed to this mailbox. */
  originally_to?: { name: string; address: string } | null;
  /** v1.5.69: every internal staff To/Cc recipient except the mailbox itself — the double-work badge. */
  internal_recipients?: { name: string; address: string }[];
}

export interface EmailAccount {
  id: string;
  user_id: string | null;
  /** Resolved from users.name via the admin list endpoint — "My Mail" owner picker. */
  owner_name?: string | null;
  mailbox_address: string;
  display_name: string | null;
  is_shared_mailbox: boolean;
  is_tammy_tasker: boolean;
  is_active: boolean;
  account_id: string | null;
  created_at: string;
  created_by: string | null;
}

export interface PinnedEmail {
  id: string;
  project_id: string;
  pinned_by_user_id: string | null;
  graph_message_id: string;
  mailbox_address: string;
  subject: string | null;
  sender_name: string | null;
  sender_email: string | null;
  snippet: string | null;
  received_at: string | null;
  pinned_at: string;
}

/** One email row in the central cross-client inbox (C1). */
export interface InboxMessage {
  id: string;
  subject: string | null;
  from: GraphEmailAddress | null;
  snippet: string | null;
  receivedDateTime: string;
  isRead: boolean;
  /** Outlook categories (named color tags) — two-way synced (093). */
  categories?: string[];
  hasAttachments: boolean;
  taskStatus?: string;
  assignedToUserId?: string;
  assignedToName?: string;
  taskNotes?: string;
  pinned?: boolean;
  pinnedProjectId?: string;
  pinnedProjectTitle?: string;
  flagStatus?: string;
  /** Graph conversationId — groups a back-and-forth into a thread (W6). */
  conversationId?: string | null;
  /** True on rows returned by GET /api/inbox/mine — claim/pin/assign are hidden for these. */
  isOwnPersonalMailbox?: boolean;
  isSharedMailbox?: boolean;
  /** ENG-98: unwrapped {name,address} lists from the archive (087) — feeds the expanded "To:" line. */
  toRecipients?: GraphEmailAddress[];
  ccRecipients?: GraphEmailAddress[];
  /** ENG-98: who a transport-rule BCC copy was originally sent to; null/absent = addressed to this mailbox. */
  originallyTo?: { name: string; address: string } | null;
  /** v1.5.69: every internal staff To/Cc recipient except the mailbox itself — the double-work badge. */
  internalRecipients?: { name: string; address: string }[];
}

/** One client mailbox group in the central inbox (C1). */
export interface InboxGroup {
  account_id: string;
  account_name: string;
  mailbox_address: string;
  display_name: string | null;
  makershubForwardConfigured: boolean;
  unread: number;
  total: number;
  messages: InboxMessage[];
  /** Noise-filtered messages (W3) — shown in a collapsible "Filtered" bucket, not deleted. */
  filtered?: InboxMessage[];
  filteredCount?: number;
}

/** A mailbox noise-filter rule (W3). mailbox_address null = firm-wide. */
export interface MailboxFilterRule {
  id: string;
  mailbox_address: string | null;
  match_field: "subject" | "sender";
  pattern: string;
  is_active: boolean;
  created_at: string;
}

/** A row from the permanent email archive (emails table) — search + Waiting On results. */
export interface ArchivedEmail {
  id: string;
  mailbox_address: string;
  graph_message_id: string;
  account_id: string | null;
  subject: string | null;
  from_name: string | null;
  from_email: string | null;
  received_at: string | null;
  has_attachments: boolean;
  is_read: boolean;
  flag_status: string | null;
  body_preview: string | null;
  conversation_id?: string | null;
  /** Search results only (090): every client this email links to (contact match or mailbox mapping). */
  matched_accounts?: Array<{ id: string; name: string }>;
}

/** An open follow-up (email_tasks row) merged with its archived email + client name. */
export interface WaitingOnItem extends ArchivedEmail {
  follow_up_at: string | null;
  follow_up_note: string | null;
  status: string;
  assigned_to_user_id: string | null;
  assignee_name: string | null;
  account_name: string | null;
}

/** One node in the "My Mail" folder rail (Phase 2). `live` = kept current by the 5-min
 * delta sync (inbox/sent only) vs backfill-only (any other folder — see emailAccounts.ts). */
export interface MailFolder {
  path: string;
  label: string;
  unreadCount: number;
  live: boolean;
  /** Graph mailFolders id — needed to rename/delete this folder or move a message into it.
   * Optional: Phase A ships the folder-mutation UI ahead of the server route that populates
   * this (Phase B adds `id` to GET /api/inbox/mine/folders); undefined until then. */
  id?: string;
}

/** One row's outcome in a bulk email action. The server applies each message independently
 * and always reports per-item, so a partial failure is visible rather than silently lost. */
export interface BulkEmailItemResult {
  mailbox: string;
  message_id: string;
  ok: boolean;
  error?: string;
  /** Server truth after a "categorize" — the merged label list for that message. */
  categories?: string[];
}

export interface BulkEmailResponse {
  ok: boolean;
  succeeded: number;
  failed: number;
  results: BulkEmailItemResult[];
}

export const emailApi = {
  /** Central cross-client inbox: every mapped mailbox grouped by client (C1). */
  async getInbox(refresh = false): Promise<InboxGroup[]> {
    const headers = await getAuthHeaders();
    const res = await fetch(`/api/inbox${refresh ? "?refresh=1" : ""}`, { headers });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to load inbox");
    return res.json();
  },

  /**
   * "Load older" pagination for a single mailbox group: pages back beyond the main
   * /api/inbox INBOX_FETCH_LIMIT (100) window via the retention archive. Read-only —
   * does not affect /api/inbox's 60s cache.
   */
  async getOlderInboxMessages(mailbox: string, before: string, limit = 100): Promise<{ mailbox: string; messages: InboxMessage[]; filtered?: InboxMessage[]; filteredCount?: number }> {
    const headers = await getAuthHeaders();
    const params = new URLSearchParams({ mailbox, before, limit: String(limit) });
    const res = await fetch(`/api/inbox/older?${params.toString()}`, { headers });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to load older messages");
    return res.json();
  },

  /** "My Mail" (Phase 2): the caller's own personal mailbox, if provisioned. `mailbox: null`
   * when no personal mailbox is assigned to this user (admin sets it in Admin Config). */
  async getMyMail(folder = "inbox", before?: string): Promise<{ mailbox: string | null; display_name?: string | null; folder?: string; messages: InboxMessage[]; filtered?: InboxMessage[]; filteredCount?: number }> {
    const headers = await getAuthHeaders();
    const params = new URLSearchParams({ folder });
    if (before) params.set("before", before);
    const res = await fetch(`/api/inbox/mine?${params.toString()}`, { headers });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to load your mail");
    return res.json();
  },

  /** Search across the caller's own personal mailbox (all folders unless `folder` is given).
   * Not routed through the firm-wide search RPC — that RPC deliberately excludes personal
   * mailboxes for everyone, including their own owner. Results ignore the caller's own
   * noise-filter rules (a search should show everything that matches, filtered or not). */
  async searchMyMail(q: string, folder?: string): Promise<{ mailbox: string | null; messages: InboxMessage[] }> {
    const headers = await getAuthHeaders();
    const params = new URLSearchParams({ q });
    if (folder) params.set("folder", folder);
    const res = await fetch(`/api/inbox/mine/search?${params.toString()}`, { headers });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to search your mail");
    return res.json();
  },

  /** Folder tree (Inbox/Sent/subfolders) for the caller's own personal mailbox. */
  async getMyMailFolders(): Promise<{ mailbox: string | null; folders: MailFolder[] }> {
    const headers = await getAuthHeaders();
    const res = await fetch("/api/inbox/mine/folders", { headers });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to load folders");
    return res.json();
  },

  /**
   * Create a new folder in the caller's own "My Mail" mailbox. `parentPath` (optional) nests
   * it under an existing folder (e.g. "Clients"); omit to create at the mailbox root.
   * Server resolves the owner's mailbox itself — never a request-supplied mailbox/user id.
   */
  async createMyMailFolder(displayName: string, parentPath?: string): Promise<{ id: string; displayName: string; path: string }> {
    const headers = await getAuthHeaders();
    const res = await fetch("/api/inbox/mine/folders", {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ displayName, parentPath }),
    });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to create folder");
    return res.json();
  },

  /** Rename a folder in the caller's own "My Mail" mailbox. */
  async renameMyMailFolder(folderId: string, displayName: string): Promise<{ ok: boolean }> {
    const headers = await getAuthHeaders();
    const res = await fetch(`/api/inbox/mine/folders/${encodeURIComponent(folderId)}`, {
      method: "PATCH",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ displayName }),
    });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to rename folder");
    return res.json();
  },

  /** Delete a folder in the caller's own "My Mail" mailbox. Outlook semantics: this MOVES
   * the folder and its contents to Deleted Items — it does not purge anything. */
  async deleteMyMailFolder(folderId: string): Promise<{ ok: boolean }> {
    const headers = await getAuthHeaders();
    const res = await fetch(`/api/inbox/mine/folders/${encodeURIComponent(folderId)}`, {
      method: "DELETE",
      headers,
    });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to delete folder");
    return res.json();
  },

  /**
   * Move a message to a different folder within the caller's own "My Mail" mailbox. Graph
   * mints a NEW message id on move — the response's `id` replaces the old one everywhere it
   * was tracked client-side (open detail pane, read-state overrides, cached rows).
   *
   * The server resolves the destination's real path from `destinationFolderId` itself (C2)
   * — it no longer trusts a client-supplied path, so none is sent here.
   */
  async moveMyMailMessage(
    mailbox: string,
    messageId: string,
    destinationFolderId: string,
  ): Promise<{ id: string }> {
    const headers = await getAuthHeaders();
    const res = await fetch(`/api/inbox/mine/messages/${encodeURIComponent(messageId)}/move`, {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ destinationFolderId }),
    });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to move message");
    return res.json();
  },

  /**
   * Flag/unflag an email as a durable "Waiting On" follow-up (Phase 4 W1).
   * Flagging: mirrors the native Outlook flag, sets emails.flag_status, and writes an
   * email_tasks follow-up (follow_up_at defaults to today server-side; pass a date to override).
   * Unflagging (flagged:false): clears all three. The follow-up survives Graph's recent window.
   */
  async setEmailFlag(data: {
    mailbox: string;
    message_id: string;
    flagged: boolean;
    account_id?: string | null;
    follow_up_at?: string | null;
    note?: string | null;
  }): Promise<{ ok: boolean }> {
    const headers = await getAuthHeaders();
    const res = await fetch("/api/emails/flag", {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to update flag");
    return res.json();
  },

  /** Noise-filter rules (W3): list firm-wide + the given mailbox's rules (or all if omitted). */
  async getFilterRules(mailbox?: string): Promise<MailboxFilterRule[]> {
    const headers = await getAuthHeaders();
    const qs = mailbox ? `?mailbox=${encodeURIComponent(mailbox)}` : "";
    const res = await fetch(`/api/mailbox-filter-rules${qs}`, { headers });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to load filter rules");
    return res.json();
  },

  async addFilterRule(data: { mailbox_address: string | null; match_field: "subject" | "sender"; pattern: string }): Promise<MailboxFilterRule> {
    const headers = await getAuthHeaders();
    const res = await fetch("/api/mailbox-filter-rules", { method: "POST", headers, body: JSON.stringify(data) });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to add rule");
    return res.json();
  },

  async toggleFilterRule(id: string, is_active: boolean): Promise<MailboxFilterRule> {
    const headers = await getAuthHeaders();
    const res = await fetch(`/api/mailbox-filter-rules/${id}`, { method: "PATCH", headers, body: JSON.stringify({ is_active }) });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to update rule");
    return res.json();
  },

  async deleteFilterRule(id: string): Promise<void> {
    const headers = await getAuthHeaders();
    const res = await fetch(`/api/mailbox-filter-rules/${id}`, { method: "DELETE", headers });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to delete rule");
  },

  /** Mark an email read/unread, mirrored to Outlook (Phase 4 W4 two-way read sync). */
  async markRead(mailbox: string, message_id: string, is_read: boolean): Promise<{ ok: boolean }> {
    const headers = await getAuthHeaders();
    const res = await fetch("/api/emails/mark-read", {
      method: "POST",
      headers,
      body: JSON.stringify({ mailbox, message_id, is_read }),
    });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to mark read");
    return res.json();
  },

  /**
   * Apply one action to many emails in a single request. The server applies each message
   * independently and reports per-item, so the caller can patch only the rows that landed
   * — a whole-batch rollback would be wrong when 98 of 100 succeeded.
   */
  async bulkEmailAction(body: {
    action: "read" | "assign" | "flag" | "categorize";
    items: { mailbox: string; message_id: string; account_id?: string | null }[];
    params: {
      is_read?: boolean;
      assigned_to_user_id?: string | null;
      flagged?: boolean;
      category?: string;
    };
  }): Promise<BulkEmailResponse> {
    const headers = await getAuthHeaders();
    const res = await fetch("/api/emails/bulk", {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error((await res.json()).message || "Bulk action failed");
    return res.json();
  },

  /**
   * Full-text search over the permanent email archive (Phase 4 W2), backed by emails.search_tsv.
   * Unlimited by date — searches all archived mail, not just the recent window.
   */
  async searchArchive(
    q: string,
    accountId?: string | null,
    opts?: { from?: string; to?: string }, // YYYY-MM-DD, "to" inclusive
  ): Promise<{ emails: ArchivedEmail[] }> {
    const headers = await getAuthHeaders();
    const params = new URLSearchParams({ q });
    if (accountId) params.set("accountId", accountId);
    if (opts?.from) params.set("from", opts.from);
    if (opts?.to) params.set("to", opts.to);
    const res = await fetch(`/api/emails/search?${params.toString()}`, { headers });
    if (!res.ok) throw new Error((await res.json()).message || "Search failed");
    return res.json();
  },

  /**
   * The "Waiting On" list (Phase 4 W1): every email with an open follow-up, joined to the
   * archive so it renders even after aging out. mine=true filters to the current user's follow-ups.
   */
  /** AI-draft a reply to an email (092): returns editable body text, nothing is sent. */
  async aiDraftReply(mailbox: string, messageId: string, staffName?: string, instruction?: string): Promise<{ draft: string }> {
    const headers = await getAuthHeaders();
    const res = await fetch(`/api/emails/ai-draft-reply`, {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ mailbox, messageId, staffName, instruction }),
    });
    if (!res.ok) throw new Error((await res.json()).message || "AI draft failed");
    return res.json();
  },

  async getWaitingOn(mine = false): Promise<WaitingOnItem[]> {
    const headers = await getAuthHeaders();
    const res = await fetch(`/api/inbox/waiting-on${mine ? "?mine=1" : ""}`, { headers });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to load Waiting On");
    return res.json();
  },

  /** Upsert an email task (claim/assign/status) by graph_message_id + mailbox. */
  async upsertEmailTask(data: {
    graph_message_id: string;
    mailbox_address: string;
    account_id: string;
    status?: string;
    assigned_to_user_id?: string | null;
    notes?: string | null;
  }): Promise<any> {
    const headers = await getAuthHeaders();
    const res = await fetch("/api/email-tasks", {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to update email task");
    return res.json();
  },

  /** Fetch emails for a client (mapped mailbox + contact-matched from any mailbox).
   * `filtered` = noise-rule matches (W3), shown in collapsible buckets on the account email tabs.
   * `sharedUnread` = de-noised unread count for the mapped mailbox (Shared Inbox tab badge).
   * `makershubForwardConfigured` = the account has a MakersHub forward address set (gates → MH). */
  async getAccountEmails(accountId: string): Promise<{
    emails: ClientEmail[];
    filtered?: ClientEmail[];
    sharedUnread?: number;
    makershubForwardConfigured?: boolean;
    warning?: string;
  }> {
    const headers = await getAuthHeaders();
    const res = await fetch(`/api/accounts/${accountId}/emails`, { headers });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to fetch emails");
    return res.json();
  },

  /** Pin an email to a project */
  async pinEmail(data: {
    project_id: string;
    graph_message_id: string;
    mailbox_address: string;
    subject?: string | null;
    sender_name?: string | null;
    sender_email?: string | null;
    snippet?: string | null;
    received_at?: string | null;
  }): Promise<PinnedEmail> {
    const headers = await getAuthHeaders();
    const res = await fetch("/api/emails/pin", {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to pin email");
    return res.json();
  },

  /** Unpin an email */
  async unpinEmail(pinnedEmailId: string): Promise<void> {
    const headers = await getAuthHeaders();
    const res = await fetch(`/api/emails/pin/${pinnedEmailId}`, { method: "DELETE", headers });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to unpin email");
  },

  /** Get all pinned emails for a project */
  async getProjectPinnedEmails(projectId: string): Promise<PinnedEmail[]> {
    const headers = await getAuthHeaders();
    const res = await fetch(`/api/projects/${projectId}/emails`, { headers });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to fetch pinned emails");
    return res.json();
  },

  /** Reply to a client email as the per-client mailbox it arrived in (C2) */
  async replyToEmail(data: {
    mailbox: string;
    message_id: string;
    account_id?: string | null;
    to: GraphEmailAddress[];
    cc?: GraphEmailAddress[];
    subject?: string;
    body: string;
    /** Reply-all: Graph derives sender + all original recipients; to/cc are ignored. */
    reply_all?: boolean;
    /** ENG-113: inline files, 2.5 MB total (same caps as compose). */
    attachments?: { name: string; contentType: string; contentBytes: string }[];
  }): Promise<{ ok: boolean }> {
    const headers = await getAuthHeaders();
    const res = await fetch("/api/emails/reply", {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to send reply");
    return res.json();
  },

  /** Set an email's Outlook categories (two-way). Pass the FULL desired list. */
  async setEmailCategories(mailbox: string, messageId: string, categories: string[]): Promise<{ ok: boolean; categories: string[] }> {
    const headers = await getAuthHeaders();
    const res = await fetch("/api/emails/categories", {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ mailbox, message_id: messageId, categories }),
    });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to set categories");
    return res.json();
  },

  /** Known firm label names (distinct categories across the archive). */
  async getCategoryNames(): Promise<{ names: string[] }> {
    const headers = await getAuthHeaders();
    const res = await fetch("/api/emails/category-names", { headers });
    if (!res.ok) throw new Error("Failed to load labels");
    return res.json();
  },

  /** Mailboxes the current staffer may Compose from (shared/mapped + any personal mailbox they own). */
  async getSendableMailboxes(): Promise<
    { mailbox_address: string; display_name: string | null; account_id: string | null; account_name: string | null; personal: boolean }[]
  > {
    const headers = await getAuthHeaders();
    const res = await fetch("/api/emails/sendable-mailboxes", { headers });
    if (!res.ok) throw new Error("Failed to load sendable mailboxes");
    return res.json();
  },

  /** Compose-from-scratch send. */
  async composeEmail(data: {
    mailbox: string;
    account_id?: string | null;
    to: string[];
    cc?: string[];
    bcc?: string[];
    subject: string;
    body: string;
    attachments?: { name: string; contentType: string; contentBytes: string }[];
  }): Promise<{ ok: boolean }> {
    const headers = await getAuthHeaders();
    const res = await fetch("/api/emails/compose", {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to send email");
    return res.json();
  },

  /** Forward an email (original attachments included) to one or more addresses. */
  async forwardEmail(mailbox: string, messageId: string, to: string[], comment?: string): Promise<{ ok: boolean; forwarded_to: string[] }> {
    const headers = await getAuthHeaders();
    const res = await fetch("/api/emails/forward", {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ mailbox, message_id: messageId, to, comment }),
    });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to forward");
    return res.json();
  },

  /** Fetch full email body on demand */
  async getEmailBody(mailbox: string, messageId: string): Promise<ClientEmail & { body?: { content: string; contentType: string } }> {
    const headers = await getAuthHeaders();
    const res = await fetch(
      `/api/emails/message?mailbox=${encodeURIComponent(mailbox)}&messageId=${encodeURIComponent(messageId)}`,
      { headers }
    );
    if (!res.ok) throw new Error((await res.json()).message || "Failed to fetch email body");
    return res.json();
  },

  // Admin: email account management
  async listEmailAccounts(): Promise<EmailAccount[]> {
    const headers = await getAuthHeaders();
    const res = await fetch("/api/admin/email-accounts", { headers });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to list email accounts");
    return res.json();
  },

  async addEmailAccount(data: {
    mailbox_address: string;
    display_name?: string;
    is_shared_mailbox?: boolean;
    is_tammy_tasker?: boolean;
    account_id?: string | null;
    user_id?: string | null;
  }): Promise<EmailAccount> {
    const headers = await getAuthHeaders();
    const res = await fetch("/api/admin/email-accounts", {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to add email account");
    return res.json();
  },

  async toggleEmailAccount(id: string, isActive: boolean): Promise<EmailAccount> {
    const headers = await getAuthHeaders();
    const res = await fetch(`/api/admin/email-accounts/${id}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify({ is_active: isActive }),
    });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to update email account");
    return res.json();
  },

  async linkEmailAccount(id: string, accountId: string | null): Promise<EmailAccount> {
    const headers = await getAuthHeaders();
    const res = await fetch(`/api/admin/email-accounts/${id}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify({ account_id: accountId }),
    });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to link email account");
    return res.json();
  },

  async updateEmailAccount(id: string, data: {
    mailbox_address?: string;
    display_name?: string;
    is_shared_mailbox?: boolean;
    is_tammy_tasker?: boolean;
    account_id?: string | null;
    user_id?: string | null;
  }): Promise<EmailAccount> {
    const headers = await getAuthHeaders();
    const res = await fetch(`/api/admin/email-accounts/${id}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to update email account");
    return res.json();
  },

  async deleteEmailAccount(id: string): Promise<void> {
    const headers = await getAuthHeaders();
    const res = await fetch(`/api/admin/email-accounts/${id}`, { method: "DELETE", headers });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to delete email account");
  },
};

// ============= EMAIL SIGNATURES (migration 107) =============

export interface EmailSignature {
  email_signature_html: string | null;
  email_signature_text: string | null;
  email_signature_enabled: boolean;
}

export interface BackupRun {
  id?: string;
  job: string;
  status: "ok" | "warning" | "error";
  started_at: string | null;
  finished_at: string;
  host: string | null;
  details: Record<string, unknown>;
}

export const backupRunsApi = {
  async list(): Promise<{ latest: BackupRun[]; recent: BackupRun[] }> {
    const headers = await getAuthHeaders();
    const res = await fetch("/api/admin/backup-runs", { headers });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || "Failed to load backup runs");
    return res.json();
  },
};

export const emailSignatureApi = {
  async get(): Promise<EmailSignature> {
    const headers = await getAuthHeaders();
    const res = await fetch("/api/users/me/email-signature", { headers });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to load signature");
    return res.json();
  },

  async save(data: { html: string | null; text: string | null; enabled: boolean }): Promise<{ ok: true; email_signature_html: string | null }> {
    const headers = await getAuthHeaders();
    const res = await fetch("/api/users/me/email-signature", {
      method: "PUT",
      headers,
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to save signature");
    return res.json();
  },

  async preview(html: string): Promise<{ html: string }> {
    const headers = await getAuthHeaders();
    const res = await fetch("/api/users/me/email-signature/preview", {
      method: "POST",
      headers,
      body: JSON.stringify({ html }),
    });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to preview signature");
    return res.json();
  },

  async uploadAsset(file: File): Promise<{ ok: true; url: string }> {
    const headers = await getAuthHeaders();
    // Don't set Content-Type — browser sets it with boundary for multipart
    const { "Content-Type": _, ...headersWithoutContentType } = headers as Record<string, string>;
    const form = new FormData();
    form.append("file", file);
    const res = await fetch("/api/users/me/signature-asset", { method: "POST", headers: headersWithoutContentType, body: form });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to upload image");
    return res.json();
  },

  async getDefaultTemplate(): Promise<{ template_html: string | null }> {
    const headers = await getAuthHeaders();
    const res = await fetch("/api/admin/email-signature-default", { headers });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to load default template");
    return res.json();
  },

  async saveDefaultTemplate(template_html: string): Promise<{ ok: true; template_html: string }> {
    const headers = await getAuthHeaders();
    const res = await fetch("/api/admin/email-signature-default", {
      method: "PUT",
      headers,
      body: JSON.stringify({ template_html }),
    });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to save default template");
    return res.json();
  },
};

// ============= MY ACCOUNTS SETTINGS (admin, v1.5.64) =============

export const myAccountsSettingsApi = {
  async get(): Promise<MyAccountsSettings & { updated_at: string | null }> {
    const headers = await getAuthHeaders();
    const res = await fetch("/api/admin/my-accounts-settings", { headers });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to load My Accounts settings");
    return res.json();
  },

  async save(settings: MyAccountsSettings): Promise<{ ok: true } & MyAccountsSettings> {
    const headers = await getAuthHeaders();
    const res = await fetch("/api/admin/my-accounts-settings", {
      method: "PUT",
      headers,
      body: JSON.stringify(settings),
    });
    if (!res.ok) throw new Error((await res.json()).message || "Failed to save My Accounts settings");
    return res.json();
  },
};

// ============= FEEDBACK =============

export interface FeedbackItem {
  id: string;
  submitter_user_id: string | null;
  submitter_name: string | null;
  type: "feature_request" | "bug" | "ux" | "look_and_feel" | "performance" | "other";
  title: string;
  description: string | null;
  status: "new" | "reviewing" | "planned" | "in_progress" | "done" | "wont_fix";
  priority: "low" | "medium" | "high" | "critical" | null;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
  vote_count: number;
  has_voted: boolean;
}

export interface FeedbackFilters {
  type?: string;
  status?: string;
  mine?: boolean;
}

export async function getFeedbackItems(filters?: FeedbackFilters): Promise<FeedbackItem[]> {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams();
  if (filters?.type) params.set("type", filters.type);
  if (filters?.status) params.set("status", filters.status);
  if (filters?.mine) params.set("mine", "true");
  const qs = params.toString();
  const res = await fetch(`/api/feedback${qs ? `?${qs}` : ""}`, { headers });
  if (!res.ok) throw new Error("Failed to fetch feedback");
  return res.json();
}

export async function createFeedbackItem(data: {
  type: string;
  title: string;
  description?: string;
}): Promise<FeedbackItem> {
  const headers = await getAuthHeaders();
  const res = await fetch("/api/feedback", {
    method: "POST",
    headers,
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error((await res.json()).message || "Failed to create feedback");
  return res.json();
}

export async function updateFeedbackItem(
  id: string,
  data: Partial<{
    type: string;
    title: string;
    description: string | null;
    status: string;
    priority: string | null;
    admin_notes: string | null;
  }>
): Promise<FeedbackItem> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/api/feedback/${id}`, {
    method: "PATCH",
    headers,
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error((await res.json()).message || "Failed to update feedback");
  return res.json();
}

export async function toggleFeedbackVote(id: string): Promise<{ voted: boolean; vote_count: number }> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/api/feedback/${id}/vote`, { method: "POST", headers });
  if (!res.ok) throw new Error("Failed to vote");
  return res.json();
}

export async function deleteFeedbackItem(id: string): Promise<void> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/api/feedback/${id}`, { method: "DELETE", headers });
  if (!res.ok) throw new Error((await res.json()).message || "Failed to delete feedback");
}

export interface FeedbackComment {
  id: string;
  feedback_item_id: string;
  author_user_id: string | null;
  author_name: string;
  body: string;
  created_at: string;
}

export async function getFeedbackComments(feedbackId: string): Promise<FeedbackComment[]> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/api/feedback/${feedbackId}/comments`, { headers });
  if (!res.ok) throw new Error("Failed to fetch comments");
  return res.json();
}

export interface FeedbackAttachment {
  id: string;
  feedback_item_id: string;
  uploaded_by_user_id: string | null;
  uploader_name: string | null;
  file_name: string;
  storage_path: string;
  file_size: number | null;
  content_type: string | null;
  created_at: string;
  signed_url: string | null;
}

export async function getFeedbackAttachments(feedbackId: string): Promise<FeedbackAttachment[]> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/api/feedback/${feedbackId}/attachments`, { headers });
  if (!res.ok) throw new Error("Failed to fetch attachments");
  return res.json();
}

export async function uploadFeedbackAttachment(feedbackId: string, file: File): Promise<FeedbackAttachment> {
  const headers = await getAuthHeaders();
  // Don't set Content-Type — browser sets it with boundary for multipart
  const { "Content-Type": _, ...headersWithoutContentType } = headers as Record<string, string>;
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`/api/feedback/${feedbackId}/attachments`, {
    method: "POST",
    headers: headersWithoutContentType,
    body: form,
  });
  if (!res.ok) throw new Error((await res.json()).message || "Upload failed");
  return res.json();
}

export async function deleteFeedbackAttachment(attachmentId: string): Promise<void> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/api/feedback/attachments/${attachmentId}`, { method: "DELETE", headers });
  if (!res.ok) throw new Error("Failed to delete attachment");
}

export async function postFeedbackComment(feedbackId: string, body: string): Promise<FeedbackComment> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/api/feedback/${feedbackId}/comments`, {
    method: "POST",
    headers,
    body: JSON.stringify({ body }),
  });
  if (!res.ok) throw new Error((await res.json()).message || "Failed to post comment");
  return res.json();
}

export async function updateFeedbackComment(commentId: string, body: string): Promise<FeedbackComment> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/api/feedback/comments/${commentId}`, {
    method: "PATCH",
    headers,
    body: JSON.stringify({ body }),
  });
  if (!res.ok) throw new Error((await res.json()).message || "Failed to update comment");
  return res.json();
}

// ---- Chat Attachments ----

export interface ChatAttachment {
  id: string;
  message_id: string;
  uploaded_by_user_id: string | null;
  uploaded_by_portal_email?: string | null;
  file_name: string;
  storage_path: string;
  file_size: number | null;
  content_type: string | null;
  created_at: string;
  signed_url: string | null;
}

// Team Chat (project_messages)
export async function getMessageAttachments(messageId: string): Promise<ChatAttachment[]> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/api/project-messages/${messageId}/attachments`, { headers });
  if (!res.ok) return [];
  return res.json();
}

export async function uploadMessageAttachment(messageId: string, file: File): Promise<ChatAttachment> {
  const headers = await getAuthHeaders();
  const { "Content-Type": _, ...headersWithoutContentType } = headers as Record<string, string>;
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(`/api/project-messages/${messageId}/attachments`, {
    method: "POST",
    headers: headersWithoutContentType,
    body: formData,
  });
  if (!res.ok) throw new Error("Failed to upload attachment");
  return res.json();
}

export async function deleteMessageAttachment(attachmentId: string): Promise<void> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/api/project-message-attachments/${attachmentId}`, { method: "DELETE", headers });
  if (!res.ok) throw new Error("Failed to delete attachment");
}

// Client Chat (client_topic_messages)
export async function getClientTopicMessageAttachments(messageId: string): Promise<ChatAttachment[]> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/api/client-topic-messages/${messageId}/attachments`, { headers });
  if (!res.ok) return [];
  return res.json();
}

export async function uploadClientTopicMessageAttachment(messageId: string, file: File): Promise<ChatAttachment> {
  const headers = await getAuthHeaders();
  const { "Content-Type": _, ...headersWithoutContentType } = headers as Record<string, string>;
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(`/api/client-topic-messages/${messageId}/attachments`, {
    method: "POST",
    headers: headersWithoutContentType,
    body: formData,
  });
  if (!res.ok) throw new Error("Failed to upload attachment");
  return res.json();
}

export async function deleteClientTopicMessageAttachment(attachmentId: string): Promise<void> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/api/client-topic-message-attachments/${attachmentId}`, { method: "DELETE", headers });
  if (!res.ok) throw new Error("Failed to delete attachment");
}

// ─── Global Search ───────────────────────────────────────────────────────────

export type SearchResultType = "client" | "project" | "task" | "contact";

export interface SearchResult {
  type: SearchResultType;
  id: string;
  title: string;
  subtitle: string;
  accountId?: string;
  projectId?: string;
}

export async function globalSearch(q: string): Promise<SearchResult[]> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/api/search?q=${encodeURIComponent(q)}`, { headers });
  if (!res.ok) return [];
  return res.json();
}

export interface RecentClient {
  id: string;
  account_name: string;
  entity_type: string | null;
}

export async function getRecentClients(): Promise<RecentClient[]> {
  const headers = await getAuthHeaders();
  const res = await fetch("/api/recent-clients", { headers });
  if (!res.ok) return [];
  return res.json();
}

// ─── Trash ───────────────────────────────────────────────────────────────────

export async function getTrashItems(type: "projects" | "tasks"): Promise<any[]> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/api/trash?type=${type}`, { headers });
  if (!res.ok) return [];
  return res.json();
}

export async function restoreTrashItem(type: "project" | "task", id: string): Promise<void> {
  const headers = await getAuthHeaders();
  await fetch("/api/trash/restore", {
    method: "POST",
    headers: { ...headers, "Content-Type": "application/json" },
    body: JSON.stringify({ type, id }),
  });
}

export async function permanentlyDeleteTrashItem(type: "project" | "task", id: string): Promise<void> {
  const headers = await getAuthHeaders();
  await fetch(`/api/trash/${type}/${id}`, { method: "DELETE", headers });
}

// ─── Email Automation Log ─────────────────────────────────────────────────────

export interface EmailAutomationEntry {
  id: string;
  graph_message_id: string;
  automation_name: string;
  automation_detail: string | null;
  triggered_at: string;
  triggered_by: string;
}

export async function getEmailAutomationLog(accountId: string): Promise<EmailAutomationEntry[]> {
  return fetchWithAuth<EmailAutomationEntry[]>(`/api/email-automation-log?accountId=${accountId}`);
}

// ─── Notifications ───────────────────────────────────────────────────────────

export type NotificationType =
  | "task_assigned"
  | "stage_changed"
  | "status_changed"
  | "mention"
  | "task_comment"
  | "project_comment"
  | "project_assigned"
  | "client_message"
  | "project_completed"
  | "client_document_upload"
  | "client_task_complete"
  | "project_ready_for_review"
  | "client_document_deleted"
  | "mention_on_note"
  | "client_chat_message"
  | "client_task_comment"
  | "task_unblocked"
  | "email_assigned"
  | "feedback_resolved"
  | "document_delete_requested"
  | "document_deleted"
  | "document_delete_decided";

export interface AppNotification {
  id: string;
  user_id: string;
  type: NotificationType;
  title: string;
  body: string | null;
  is_read: boolean;
  related_project_id: string | null;
  related_task_id: string | null;
  /** Client-chat topic (client_chat_message) — lets the bell open the exact conversation */
  related_topic_id?: string | null;
  /** Enriched server-side from related_project_id → projects → accounts */
  account_id: string | null;
  account_name: string | null;
  tax_year: number | null;
  period_label: string | null;
  project_type: string | null;
  created_at: string;
}

/**
 * Build the in-app destination for a notification. Chat notifications deep-link
 * straight to the conversation (?tab=messages/?tab=client-chat); task-style
 * notifications keep the ?openProject/?openTask pattern. Returns null when the
 * notification has nowhere to go.
 */
export function notificationTargetUrl(n: AppNotification): string | null {
  // Feedback & Ideas resolution — notifications carry no feedback-item relation
  // column, so land on the Feedback page (the email button deep-links ?item=).
  if (n.type === "feedback_resolved") return "/feedback";
  // Document deletes / delete requests land on the client's Documents tab.
  if (n.type === "document_delete_requested" || n.type === "document_deleted" || n.type === "document_delete_decided") {
    return n.account_id ? `/accounts/${n.account_id}?tab=documents` : null;
  }
  // Shared-inbox assignment — notifications carry no email-identity column, so land on the
  // client's Shared Inbox tab (the email button deep-links to the message itself via ?email=).
  if (n.type === "email_assigned") {
    return n.account_id ? `/accounts/${n.account_id}?tab=shared-inbox` : "/inbox";
  }
  if (n.account_id) {
    // Client portal chat → Client Chat tab (topic preselected when known;
    // rows written before migration 098 have no topic id and just open the tab)
    if (n.type === "client_chat_message") {
      const topic = n.related_topic_id ? `&topic=${n.related_topic_id}` : "";
      return `/accounts/${n.account_id}?tab=client-chat${topic}`;
    }
    // Team chat message, or a team-chat @mention (mentions on a task comment
    // always carry related_task_id; team-chat mentions never do)
    const isTeamChat =
      n.related_project_id &&
      (n.type === "project_comment" || (n.type === "mention" && !n.related_task_id));
    if (isTeamChat) {
      return `/accounts/${n.account_id}?tab=messages&chatProject=${n.related_project_id}`;
    }
    const params = new URLSearchParams();
    if (n.related_project_id) params.set("openProject", n.related_project_id);
    if (n.related_task_id) params.set("openTask", n.related_task_id);
    const qs = params.toString();
    return `/accounts/${n.account_id}${qs ? `?${qs}` : ""}`;
  }
  if (n.related_project_id) return "/projects";
  return null;
}

export interface UserNotifPref {
  notification_type: NotificationType;
  in_app: boolean;
  email_enabled: boolean;
}

export async function getNotificationPrefs(): Promise<UserNotifPref[]> {
  const headers = await getAuthHeaders();
  const res = await fetch("/api/users/notification-prefs", { headers });
  if (!res.ok) return [];
  return res.json();
}

export async function setNotificationPref(
  notification_type: NotificationType,
  in_app: boolean,
  email_enabled: boolean
): Promise<void> {
  const headers = await getAuthHeaders();
  const res = await fetch("/api/users/notification-prefs", {
    method: "PUT",
    headers: { ...headers, "Content-Type": "application/json" },
    body: JSON.stringify({ notification_type, in_app, email_enabled }),
  });
  // Surface failures so the Settings error toast is reachable — a swallowed 500
  // here is exactly how the email_assigned constraint bug went unnoticed (v1.3.44).
  if (!res.ok) throw new Error(`Failed to save notification preference (${res.status})`);
}

export interface NotificationsResponse {
  notifications: AppNotification[];
  unread_count: number;
}

export async function getNotifications(): Promise<NotificationsResponse> {
  const headers = await getAuthHeaders();
  const res = await fetch("/api/notifications", { headers });
  if (!res.ok) return { notifications: [], unread_count: 0 };
  return res.json();
}

export async function markNotificationRead(id: string): Promise<void> {
  const headers = await getAuthHeaders();
  await fetch(`/api/notifications/${id}/read`, { method: "PATCH", headers });
}

export async function markNotificationUnread(id: string): Promise<void> {
  const headers = await getAuthHeaders();
  await fetch(`/api/notifications/${id}/unread`, { method: "PATCH", headers });
}

export async function markAllNotificationsRead(): Promise<void> {
  const headers = await getAuthHeaders();
  await fetch("/api/notifications/read-all", { method: "PATCH", headers });
}

export async function deleteNotification(id: string): Promise<void> {
  const headers = await getAuthHeaders();
  await fetch(`/api/notifications/${id}`, { method: "DELETE", headers });
}

export async function clearReadNotifications(): Promise<void> {
  const headers = await getAuthHeaders();
  await fetch("/api/notifications/read", { method: "DELETE", headers });
}

// ─── Financial Sources ───────────────────────────────────────────────────────

export interface FinancialSource {
  id: string;
  account_id: string;
  source_type: string;
  institution_name: string;
  account_nickname: string | null;
  external_hint: string | null;
  expected_frequency: string;
  assigned_user_id: string | null;
  assigned_user_name?: string | null;
  last_pulled_date: string | null;
  through_date: string | null;
  status: "ok" | "attention" | "overdue";
  notes: string | null;
  statement_day: number | null;
  login_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface SourcePullLog {
  id: string;
  financial_source_id: string;
  pulled_by: string;
  pulled_by_name?: string | null;
  pull_date: string;
  through_date: string | null;
  note: string | null;
  created_at: string;
}

export async function getFinancialSources(accountId?: string): Promise<FinancialSource[]> {
  const headers = await getAuthHeaders();
  const url = accountId && accountId !== "all"
    ? `/api/financial-sources?accountId=${accountId}`
    : "/api/financial-sources";
  const res = await fetch(url, { headers });
  if (!res.ok) throw new Error("Failed to fetch financial sources");
  return res.json();
}

export async function createFinancialSource(data: {
  account_id: string;
  source_type: string;
  institution_name: string;
  account_nickname?: string | null;
  external_hint?: string | null;
  expected_frequency?: string;
  assigned_user_id?: string | null;
  status?: string;
  notes?: string | null;
  statement_day?: number | null;
  login_url?: string | null;
}): Promise<FinancialSource> {
  const headers = await getAuthHeaders();
  const res = await fetch("/api/financial-sources", {
    method: "POST",
    headers,
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to create financial source");
  return res.json();
}

export async function updateFinancialSource(id: string, data: Partial<FinancialSource>): Promise<FinancialSource> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/api/financial-sources/${id}`, {
    method: "PATCH",
    headers,
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to update financial source");
  return res.json();
}

export async function deleteFinancialSource(id: string): Promise<void> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/api/financial-sources/${id}`, { method: "DELETE", headers });
  if (!res.ok) throw new Error("Failed to delete financial source");
}

export async function markSourcePulled(id: string, data: { through_date?: string; note?: string }): Promise<void> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/api/financial-sources/${id}/mark-pulled`, {
    method: "POST",
    headers,
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to mark source as pulled");
}

export async function getSourcePullLog(id: string): Promise<SourcePullLog[]> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/api/financial-sources/${id}/pull-log`, { headers });
  if (!res.ok) throw new Error("Failed to fetch pull log");
  return res.json();
}

// ─── Client Vault ────────────────────────────────────────────────────────────

export interface VaultField {
  label: string;
  value: string;
}

export interface VaultRecord {
  id: string;
  account_id: string;
  name: string;
  username: string | null;
  password: string | null;
  url: string | null;
  notes: string | null;
  fields: VaultField[];
  fc_vault_id: string | null;
  created_at: string;
  updated_at: string | null;
}

export interface VaultRecordInput {
  name: string;
  username?: string;
  password?: string;
  url?: string;
  notes?: string;
  fields?: VaultField[];
}

export async function getVaultRecords(accountId: string): Promise<VaultRecord[]> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/api/accounts/${accountId}/vault`, { headers });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(`${res.status}: ${body.message ?? "Failed to fetch vault records"}`);
  }
  return res.json();
}

export async function createVaultRecord(accountId: string, data: VaultRecordInput): Promise<VaultRecord> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/api/accounts/${accountId}/vault`, {
    method: "POST",
    headers,
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to create vault record");
  return res.json();
}

export async function updateVaultRecord(id: string, data: Partial<VaultRecordInput>): Promise<VaultRecord> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/api/vault/${id}`, {
    method: "PATCH",
    headers,
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to update vault record");
  return res.json();
}

export async function deleteVaultRecord(id: string): Promise<void> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/api/vault/${id}`, { method: "DELETE", headers });
  if (!res.ok) throw new Error("Failed to delete vault record");
}

// ── SmartVault Integration (admin only) ──────────────────────────────────────

export interface SVConfigured {
  clientId: boolean;
  privateKey: boolean;
  adminEmail: boolean;
  accountId: boolean;
}

export interface SVStatus {
  connected: boolean;
  accountId?: string;
  adminEmail?: string;
  configured: SVConfigured;
  error?: string;
}

export interface SVNode {
  name: string;
  uri: string;
  type: "folder" | "file";
  modifiedOn?: string;
  createdOn?: string;
  modifiedBy?: string;
  size?: number;
  downloadUri?: string;
}

export interface SVStagedUpload {
  id: string;
  name: string;
  file_path: string;
  file_size: number | null;
  file_type: string | null;
  created_at: string;
  task_id: string;
  task_title: string | null;
}

export async function getSmartVaultStatus(): Promise<SVStatus> {
  const headers = await getAuthHeaders();
  const res = await fetch("/api/smartvault/status", { headers });
  if (!res.ok) throw new Error("Failed to get SmartVault status");
  return res.json();
}

export async function testSmartVaultConnection(): Promise<{ success: boolean; message: string }> {
  const headers = await getAuthHeaders();
  const res = await fetch("/api/smartvault/test-connection", { method: "POST", headers });
  if (!res.ok) throw new Error("Test connection request failed");
  return res.json();
}

export async function provisionSmartVaultClient(accountId: string): Promise<{ sv_client_id: string; name: string }> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/api/smartvault/accounts/${accountId}/provision`, { method: "POST", headers });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as any).message ?? "Provisioning failed");
  }
  return res.json();
}

export async function getSmartVaultVault(accountId: string, path?: string): Promise<SVNode[]> {
  const headers = await getAuthHeaders();
  const url = path
    ? `/api/smartvault/accounts/${accountId}/vault?path=${encodeURIComponent(path)}`
    : `/api/smartvault/accounts/${accountId}/vault`;
  const res = await fetch(url, { headers });
  if (!res.ok) throw new Error("Failed to browse SmartVault vault");
  return res.json();
}

export async function getSmartVaultDocuments(accountId: string, folderPath: string): Promise<SVNode[]> {
  const headers = await getAuthHeaders();
  const res = await fetch(
    `/api/smartvault/accounts/${accountId}/documents?path=${encodeURIComponent(folderPath)}`,
    { headers }
  );
  if (!res.ok) throw new Error("Failed to list SmartVault documents");
  return res.json();
}

export async function getSmartVaultStagedUploads(accountId: string): Promise<SVStagedUpload[]> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/api/smartvault/accounts/${accountId}/staged-uploads`, { headers });
  if (!res.ok) throw new Error("Failed to load staged uploads");
  return res.json();
}

export async function deleteSmartVaultFile(accountId: string, uri: string): Promise<void> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/api/smartvault/accounts/${accountId}/files`, {
    method: "DELETE",
    headers,
    body: JSON.stringify({ uri }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as any).message ?? "Delete failed");
  }
}

export async function uploadToSmartVault(accountId: string, folderPath: string, file: File): Promise<void> {
  const { getSupabase } = await import("./supabase");
  const supabase = await getSupabase();
  const { data: { session } } = await supabase.auth.getSession();
  const formData = new FormData();
  formData.append("file", file);
  formData.append("folderPath", folderPath);
  const res = await fetch(`/api/smartvault/accounts/${accountId}/upload`, {
    method: "POST",
    headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {},
    body: formData,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as any).message ?? "Upload failed");
  }
}

export async function moveStagedToSmartVault(accountId: string, attachmentId: string, folderPath: string): Promise<void> {
  const headers = await getAuthHeaders();
  const res = await fetch(`/api/smartvault/accounts/${accountId}/move-staged`, {
    method: "POST",
    headers,
    body: JSON.stringify({ attachmentId, folderPath }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as any).message ?? "Move failed");
  }
}

export async function moveSmartVaultFile(fileUri: string, targetFolderUri: string): Promise<void> {
  const headers = await getAuthHeaders();
  const res = await fetch("/api/smartvault/files/move", {
    method: "POST",
    headers,
    body: JSON.stringify({ fileUri, targetFolderUri }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as any).message ?? "Move failed");
  }
}

export async function getSmartVaultFileUrl(accountId: string, uri: string): Promise<string> {
  const headers = await getAuthHeaders();
  const res = await fetch(
    `/api/smartvault/accounts/${accountId}/file-url?uri=${encodeURIComponent(uri)}`,
    { headers }
  );
  if (!res.ok) throw new Error("Failed to get file URL");
  const { url } = await res.json();
  return url;
}

export interface TimeReportRow {
  userId: string;
  userName: string;
  totalMinutes: number;
  billableMinutes: number;
  billablePercent: number;
  entriesCount: number;
}

export interface CapacityReportRow {
  userId: string;
  userName: string;
  weeklyCapacityHours: number;
  loggedMinutes: number;
  loggedHours: number;
  utilizationPercent: number;
  isOver: boolean;
}

export interface CapacityReport {
  weekStart: string;
  weekEnd: string;
  rows: CapacityReportRow[];
}

export async function getTimeReport(params: { dateFrom: string; dateTo: string; userId?: string }): Promise<TimeReportRow[]> {
  const qs = new URLSearchParams({ dateFrom: params.dateFrom, dateTo: params.dateTo });
  if (params.userId) qs.set("userId", params.userId);
  return fetchWithAuth<TimeReportRow[]>(`/api/reports/time?${qs}`);
}

export async function getCapacityReport(weekOf?: string): Promise<CapacityReport> {
  const qs = weekOf ? `?weekOf=${weekOf}` : "";
  return fetchWithAuth<CapacityReport>(`/api/reports/capacity${qs}`);
}

export async function getCalendarEvents(start: string, end: string, targetEmail?: string): Promise<{ events: any[]; configured: boolean }> {
  const params = new URLSearchParams({ start, end });
  if (targetEmail) params.set("targetEmail", targetEmail);
  return fetchWithAuth<{ events: any[]; configured: boolean }>(
    `/api/calendar/events?${params}`
  );
}

export async function createCalendarEvent(payload: {
  subject: string;
  date: string;
  startTime?: string;
  endTime?: string;
  isAllDay: boolean;
  showAs: "free" | "busy" | "tentative" | "oof" | "workingElsewhere";
  notes?: string;
  targetEmail?: string;
}): Promise<any> {
  return fetchWithAuth<any>("/api/calendar/events", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

// ─── Account Services ────────────────────────────────────────────────────────

export interface AccountService {
  id: string;
  account_id: string;
  name: string;
  url: string | null;
  category: string | null;
  description: string | null;
  sort_order: number;
  created_at: string;
}

export async function getAccountServices(accountId: string): Promise<AccountService[]> {
  return fetchWithAuth<AccountService[]>(`/api/accounts/${accountId}/services`);
}

export async function createAccountService(
  accountId: string,
  data: { name: string; url?: string; category?: string; description?: string }
): Promise<AccountService> {
  return fetchWithAuth<AccountService>(`/api/accounts/${accountId}/services`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateAccountService(
  accountId: string,
  serviceId: string,
  data: Partial<{ name: string; url: string | null; category: string; description: string | null; sort_order: number }>
): Promise<AccountService> {
  return fetchWithAuth<AccountService>(`/api/accounts/${accountId}/services/${serviceId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteAccountService(accountId: string, serviceId: string): Promise<void> {
  return fetchWithAuth<void>(`/api/accounts/${accountId}/services/${serviceId}`, {
    method: "DELETE",
  });
}

// ─── Email Templates (admin) ─────────────────────────────────────────────────

export interface EmailTemplate {
  id: string;
  title: string;
  subject: string;
  body: string;
  fc_template_id: number | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export async function getEmailTemplates(): Promise<EmailTemplate[]> {
  return fetchWithAuth<EmailTemplate[]>("/api/admin/email-templates");
}

export async function createEmailTemplate(data: { title: string; subject: string; body: string }): Promise<EmailTemplate> {
  return fetchWithAuth<EmailTemplate>("/api/admin/email-templates", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateEmailTemplate(id: string, data: { title: string; subject: string; body: string }): Promise<EmailTemplate> {
  return fetchWithAuth<EmailTemplate>(`/api/admin/email-templates/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteEmailTemplate(id: string): Promise<void> {
  return fetchWithAuth<void>(`/api/admin/email-templates/${id}`, { method: "DELETE" });
}

// ─── Automation Rules (admin) ─────────────────────────────────────────────────

export interface AutomationRuleEnriched {
  id: string;
  scope_task_id: string | null;
  scope_section_id: string | null;
  action_type: "add_tag" | "remove_tag" | "send_email" | "create_project";
  action_ref_id: string | null;
  action_config: Record<string, unknown>;
  fc_rule_id: number | null;
  created_at: string;
  scopeName: string;
  scopeProjectName: string;
  targetName: string;
  lastFiredAt: string | null;
  fireCount: number;
}

export async function getAdminAutomationRules(): Promise<AutomationRuleEnriched[]> {
  return fetchWithAuth<AutomationRuleEnriched[]>("/api/admin/automation-rules");
}

export async function createAdminAutomationRule(data: {
  scope_task_id?: string;
  scope_section_id?: string;
  action_type: string;
  action_ref_id?: string;
  action_config?: Record<string, unknown>;
}): Promise<AutomationRuleEnriched> {
  return fetchWithAuth<AutomationRuleEnriched>("/api/admin/automation-rules", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function deleteAdminAutomationRule(id: string): Promise<void> {
  return fetchWithAuth<void>(`/api/admin/automation-rules/${id}`, { method: "DELETE" });
}

// ─── Per-Section Automation Rules ────────────────────────────────────────────

export interface SectionRule {
  id: string;
  scope_section_id: string;
  action_type: string;          // add_tag | remove_tag
  action_ref_id: string | null;
  action_config: Record<string, unknown>;
  action_name: string | null;   // tag name
  action_color: string | null;  // tag color hex
  is_template_rule: boolean;    // true if inherited from template section
}

export async function getSectionRules(sectionId: string): Promise<SectionRule[]> {
  return fetchWithAuth<SectionRule[]>(`/api/project-sections/${sectionId}/rules`);
}

export async function createSectionRule(sectionId: string, data: {
  action_type: string;
  action_ref_id?: string;
  action_config?: Record<string, unknown>;
}): Promise<SectionRule> {
  return fetchWithAuth<SectionRule>(`/api/project-sections/${sectionId}/rules`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function deleteSectionRule(sectionId: string, ruleId: string): Promise<void> {
  return fetchWithAuth<void>(`/api/project-sections/${sectionId}/rules/${ruleId}`, { method: "DELETE" });
}

// ─── Per-Task Automation Rules ────────────────────────────────────────────────

export interface TaskRule {
  id: string;
  scope_task_id: string;
  action_type: string;
  action_ref_id: string | null;
  action_config: Record<string, unknown> | null;
  action_name: string | null;
  action_color: string | null;
  is_template_rule: boolean;
}

export async function getTaskRules(taskId: string): Promise<TaskRule[]> {
  return fetchWithAuth<TaskRule[]>(`/api/project-tasks/${taskId}/rules`);
}

export async function createTaskRule(taskId: string, data: {
  action_type: string;
  action_ref_id?: string;
  action_config?: Record<string, unknown>;
}): Promise<TaskRule> {
  return fetchWithAuth<TaskRule>(`/api/project-tasks/${taskId}/rules`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function deleteTaskRule(taskId: string, ruleId: string): Promise<void> {
  return fetchWithAuth<void>(`/api/project-tasks/${taskId}/rules/${ruleId}`, { method: "DELETE" });
}

// ─── Template Task Automation Rules ──────────────────────────────────────────

export interface TemplateTaskRule {
  id: string;
  scope_template_task_id: string;
  action_type: string;
  action_ref_id: string | null;
  action_config: Record<string, unknown> | null;
  action_name: string | null;
  action_color: string | null;
  is_template_rule: false;
}

export async function getTemplateTaskRules(templateTaskId: string): Promise<TemplateTaskRule[]> {
  return fetchWithAuth<TemplateTaskRule[]>(`/api/template-tasks/${templateTaskId}/rules`);
}

export async function createTemplateTaskRule(templateTaskId: string, data: {
  action_type: string;
  action_ref_id?: string;
  action_config?: Record<string, unknown>;
}): Promise<TemplateTaskRule> {
  return fetchWithAuth<TemplateTaskRule>(`/api/template-tasks/${templateTaskId}/rules`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function deleteTemplateTaskRule(templateTaskId: string, ruleId: string): Promise<void> {
  return fetchWithAuth<void>(`/api/template-tasks/${templateTaskId}/rules/${ruleId}`, { method: "DELETE" });
}


// ── CLIENT DOCUMENTS (Cloudflare R2) ──────────────────────────────────────────

export interface ClientDocument {
  id: string;
  account_id: string;
  uploaded_by: string | null;
  folder_path: string;
  file_name: string;
  file_size: number | null;
  content_type: string | null;
  r2_key: string;
  is_client_visible: boolean;
  notes: string | null;
  download_count: number;
  last_downloaded_at: string | null;
  visible_to_emails?: string[] | null;
  uploaded_at: string;
  uploader?: { name: string; email: string } | null;
}

export interface DocumentFolder {
  id: string;
  account_id: string;
  name: string;
  parent_id: string | null;
  path: string;
  created_at: string;
  // client-side computed:
  children?: DocumentFolder[];
  fileCount?: number;
}

export interface DocumentRequest {
  id: string;
  account_id: string;
  requested_by: string | null;
  title: string;
  notes: string | null;
  folder_path: string;
  status: "pending" | "fulfilled" | "cancelled";
  due_date: string | null;
  fulfilled_document_id: string | null;
  created_at: string;
  fulfilled_at: string | null;
  requester?: { name: string; email: string } | null;
}

// ── Folder API ───────────────────────────────────────────────────────────────

export async function getDocumentFolders(accountId: string): Promise<DocumentFolder[]> {
  return fetchWithAuth<DocumentFolder[]>(`/api/accounts/${accountId}/folders`);
}

export async function createDocumentFolder(
  accountId: string,
  data: { name: string; parentId?: string | null },
): Promise<DocumentFolder> {
  return fetchWithAuth(`/api/accounts/${accountId}/folders`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function scaffoldDocumentFolders(
  accountId: string,
  accountType: "business" | "individual",
): Promise<void> {
  return fetchWithAuth(`/api/accounts/${accountId}/folders/scaffold`, {
    method: "POST",
    body: JSON.stringify({ accountType }),
  });
}

export async function deleteDocumentFolder(accountId: string, folderId: string): Promise<void> {
  return fetchWithAuth(`/api/accounts/${accountId}/folders/${folderId}`, { method: "DELETE" });
}

export async function addYearToFolder(accountId: string, folderId: string, year: number): Promise<void> {
  return fetchWithAuth(`/api/accounts/${accountId}/folders/${folderId}/add-year`, {
    method: "POST",
    body: JSON.stringify({ year }),
  });
}

// ── Document API ─────────────────────────────────────────────────────────────

export async function getClientDocuments(accountId: string, folderPath?: string): Promise<ClientDocument[]> {
  const qs = folderPath ? `?folderPath=${encodeURIComponent(folderPath)}` : "";
  return fetchWithAuth<ClientDocument[]>(`/api/accounts/${accountId}/documents${qs}`);
}

export async function getDocumentUploadUrl(
  accountId: string,
  fileName: string,
  contentType: string,
  folderPath: string,
  fileSize?: number,
): Promise<{ uploadUrl: string; intentId: string }> {
  return fetchWithAuth(`/api/accounts/${accountId}/documents/upload-url`, {
    method: "POST",
    body: JSON.stringify({ fileName, contentType, folderPath, fileSize }),
  });
}

export async function recordDocument(
  accountId: string,
  // v1.5.101: the server built and remembers the key; only the intent id comes back.
  data: { intentId: string; notes?: string },
): Promise<ClientDocument> {
  return fetchWithAuth(`/api/accounts/${accountId}/documents`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getDocumentDownloadUrl(
  accountId: string,
  docId: string,
  mode: "download" | "view" = "download",
): Promise<{ downloadUrl: string }> {
  return fetchWithAuth(`/api/accounts/${accountId}/documents/${docId}/download-url?mode=${mode}`);
}

export async function updateClientDocument(
  accountId: string,
  docId: string,
  data: { notes?: string; folderPath?: string; visible_to_emails?: string[] | null },
): Promise<ClientDocument> {
  return fetchWithAuth(`/api/accounts/${accountId}/documents/${docId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteClientDocument(accountId: string, docId: string): Promise<void> {
  return fetchWithAuth(`/api/accounts/${accountId}/documents/${docId}`, { method: "DELETE" });
}

export interface TrashedDocument {
  id: string;
  folder_path: string;
  file_name: string;
  file_size: number | null;
  content_type: string | null;
  deleted_at: string;
  deleted_by: string | null;
}

export async function getDocumentTrash(accountId: string): Promise<TrashedDocument[]> {
  return fetchWithAuth<TrashedDocument[]>(`/api/accounts/${accountId}/documents-trash`);
}

export async function restoreClientDocument(accountId: string, docId: string): Promise<void> {
  return fetchWithAuth(`/api/accounts/${accountId}/documents/${docId}/restore`, { method: "POST" });
}

export interface DeleteRequest {
  id: string;
  document_id: string;
  account_id: string;
  requested_by: string;
  requested_by_name: string | null;
  reason: string;
  status: "pending" | "approved" | "rejected" | "cancelled";
  decided_by: string | null;
  decided_at: string | null;
  decision_note: string | null;
  created_at: string;
  file_name: string | null;
  folder_path: string | null;
}

export async function requestDocumentDelete(accountId: string, docId: string, reason: string): Promise<DeleteRequest> {
  return fetchWithAuth(`/api/accounts/${accountId}/documents/${docId}/delete-requests`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export async function getDeleteRequests(accountId: string): Promise<DeleteRequest[]> {
  return fetchWithAuth<DeleteRequest[]>(`/api/accounts/${accountId}/delete-requests`);
}

export async function approveDeleteRequest(requestId: string): Promise<void> {
  return fetchWithAuth(`/api/document-delete-requests/${requestId}/approve`, { method: "POST" });
}

export async function rejectDeleteRequest(requestId: string, note?: string): Promise<void> {
  return fetchWithAuth(`/api/document-delete-requests/${requestId}/reject`, {
    method: "POST",
    body: JSON.stringify({ note: note || undefined }),
  });
}

export async function cancelDeleteRequest(requestId: string): Promise<void> {
  return fetchWithAuth(`/api/document-delete-requests/${requestId}/cancel`, { method: "POST" });
}

export async function getDocumentRequests(accountId: string): Promise<DocumentRequest[]> {
  return fetchWithAuth<DocumentRequest[]>(`/api/accounts/${accountId}/document-requests`);
}

export async function createDocumentRequest(
  accountId: string,
  data: { title: string; notes?: string; folderPath?: string; dueDate?: string },
): Promise<DocumentRequest> {
  return fetchWithAuth(`/api/accounts/${accountId}/document-requests`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateDocumentRequest(
  accountId: string,
  reqId: string,
  data: { status?: string; fulfilledDocumentId?: string },
): Promise<DocumentRequest> {
  return fetchWithAuth(`/api/accounts/${accountId}/document-requests/${reqId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteDocumentRequest(accountId: string, reqId: string): Promise<void> {
  return fetchWithAuth(`/api/accounts/${accountId}/document-requests/${reqId}`, { method: "DELETE" });
}

export async function generateDocumentUploadToken(
  accountId: string,
  reqId: string,
): Promise<{ uploadUrl: string; expiresAt: string }> {
  return fetchWithAuth(`/api/accounts/${accountId}/document-requests/${reqId}/upload-token`, {
    method: "POST",
  });
}

// ============= MEETINGS =============
export interface Meeting {
  id: string;
  account_id: string;
  project_id: string | null;
  zoom_meeting_id: number | null;
  topic: string;
  join_url: string | null;
  start_url: string | null;
  scheduled_at: string | null;
  duration_minutes: number;
  status: string; // scheduled | started | ended | cancelled
  created_by: string | null;
  created_at: string;
  transcript_text: string | null;
  summary_text: string | null;
  recording_url: string | null;
  transcript_fetched_at: string | null;
}

export async function getMeetings(accountId: string): Promise<Meeting[]> {
  return fetchWithAuth<Meeting[]>(`/api/accounts/${accountId}/meetings`);
}

export async function createMeeting(
  accountId: string,
  data: {
    topic: string;
    scheduled_at: string;
    duration_minutes?: number;
    project_id?: string | null;
    host_email?: string;
    invitee_emails?: string[];
    alternative_host_emails?: string[];
    staff_attendee_emails?: string[];
    guest_emails?: string[];
  },
): Promise<Meeting> {
  return fetchWithAuth<Meeting>(`/api/accounts/${accountId}/meetings`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getMeetingFreshStartUrl(meetingId: string): Promise<string> {
  const data = await fetchWithAuth<{ start_url: string }>(`/api/meetings/${meetingId}/start-url`);
  return data.start_url;
}

// ─── Agreement Templates ──────────────────────────────────────────────────────

export interface TemplateVariable {
  name: string;
  label: string;
  default_value?: string;
}

export interface OptionalBlock {
  name: string;
  label: string;
  content: string;
  default_enabled: boolean;
}

export interface AgreementTemplate {
  id: string;
  name: string;
  description?: string | null;
  body: string;
  variables: TemplateVariable[];
  optional_blocks: OptionalBlock[];
  created_by?: string | null;
  created_at: string;
  updated_at: string;
}

export type AgreementTemplateInput = Omit<AgreementTemplate, "id" | "created_by" | "created_at" | "updated_at">;

export async function getAgreementTemplates(): Promise<AgreementTemplate[]> {
  return fetchWithAuth<AgreementTemplate[]>("/api/agreement-templates");
}

export async function getAgreementTemplate(id: string): Promise<AgreementTemplate> {
  return fetchWithAuth<AgreementTemplate>(`/api/agreement-templates/${id}`);
}

export async function createAgreementTemplate(data: AgreementTemplateInput): Promise<AgreementTemplate> {
  return fetchWithAuth<AgreementTemplate>("/api/agreement-templates", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateAgreementTemplate(id: string, data: Partial<AgreementTemplateInput>): Promise<AgreementTemplate> {
  return fetchWithAuth<AgreementTemplate>(`/api/agreement-templates/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteAgreementTemplate(id: string): Promise<void> {
  return fetchWithAuth(`/api/agreement-templates/${id}`, { method: "DELETE" });
}

// ─── Signature Requests ───────────────────────────────────────────────────────

export interface SignatureSigner {
  id?: string;
  name: string;
  email: string;
  status?: string;
  signed_at?: string;
}

export interface SignatureRequest {
  id: string;
  account_id: string;
  template_id?: string | null;
  signwell_document_id?: string | null;
  title: string;
  status: "pending" | "awaiting_signature" | "completed" | "declined" | "expired" | "voided";
  signers: SignatureSigner[];
  variable_values: Record<string, string>;
  enabled_blocks: string[];
  signwell_audit_trail_url?: string | null;
  signwell_document_url?: string | null;
  completed_at?: string | null;
  created_by?: string | null;
  created_at: string;
  // joined fields
  accounts?: { id: string; account_name: string } | null;
  agreement_templates?: { id: string; name: string } | null;
}

export async function getSignatureRequests(statusFilter?: string): Promise<SignatureRequest[]> {
  const qs = statusFilter && statusFilter !== "all" ? `?status=${statusFilter}` : "";
  return fetchWithAuth<SignatureRequest[]>(`/api/signature-requests${qs}`);
}

export async function getAccountSignatureRequests(accountId: string): Promise<SignatureRequest[]> {
  return fetchWithAuth<SignatureRequest[]>(`/api/accounts/${accountId}/signature-requests`);
}

export async function sendForSignature(
  accountId: string,
  data: {
    template_id?: string;
    title: string;
    signers: Array<{ name: string; email: string }>;
    variable_values?: Record<string, string>;
    enabled_blocks?: string[];
    subject?: string;
    message?: string;
    test_mode?: boolean;
    body?: string;
  },
): Promise<SignatureRequest> {
  return fetchWithAuth<SignatureRequest>(`/api/accounts/${accountId}/signature-requests`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function sendSignatureReminder(requestId: string): Promise<void> {
  return fetchWithAuth(`/api/signature-requests/${requestId}/remind`, { method: "POST" });
}

export async function voidSignatureRequest(requestId: string): Promise<void> {
  return fetchWithAuth(`/api/signature-requests/${requestId}`, { method: "DELETE" });
}

export async function syncSignatureRequest(requestId: string): Promise<{ status: string; signers: SignatureSigner[] }> {
  return fetchWithAuth(`/api/signature-requests/${requestId}/sync`, { method: "POST" });
}

// ── User Favorites ──────────────────────────────────────────────────────────

export interface UserFavorite {
  id: string;
  label: string;
  url: string;
  sort_order: number;
}

export async function getFavorites(): Promise<UserFavorite[]> {
  return fetchWithAuth("/api/user/favorites");
}

export async function createFavorite(label: string, url: string): Promise<UserFavorite> {
  return fetchWithAuth("/api/user/favorites", { method: "POST", body: JSON.stringify({ label, url }) });
}

export async function updateFavorite(id: string, updates: Partial<Pick<UserFavorite, "label" | "url" | "sort_order">>): Promise<UserFavorite> {
  return fetchWithAuth(`/api/user/favorites/${id}`, { method: "PATCH", body: JSON.stringify(updates) });
}

export async function deleteFavorite(id: string): Promise<void> {
  return fetchWithAuth(`/api/user/favorites/${id}`, { method: "DELETE" });
}

// ============================================================
// TELLER BANK CONNECTIONS
// ============================================================

export interface BankConnection {
  id: string;
  financial_source_id: string | null;
  teller_enrollment_id: string;
  teller_account_id: string;
  teller_institution: string | null;
  status: "active" | "disconnected";
  last_synced_at: string | null;
  created_at: string;
  carpco_os_client_id: string | null;
  balance: string | null;
  balance_as_of: string | null;
}

export interface PulledTransaction {
  id: string;
  teller_transaction_id: string;
  amount: string;
  date: string;
  description: string;
  status: "posted" | "pending";
  type: string | null;
  category: string | null;
}

export async function createBankConnectToken(
  accountId: string,
  financialSourceId?: string
): Promise<{ url: string }> {
  return fetchWithAuth(`/api/accounts/${accountId}/bank-connect-tokens`, {
    method: "POST",
    body: JSON.stringify({ financialSourceId }),
  });
}

export async function getBankConnections(accountId: string): Promise<BankConnection[]> {
  return fetchWithAuth(`/api/accounts/${accountId}/bank-connections`);
}

export async function syncBankConnection(connectionId: string): Promise<void> {
  return fetchWithAuth(`/api/bank-connections/${connectionId}/sync`, { method: "POST" });
}

export async function disconnectBankConnection(connectionId: string): Promise<void> {
  return fetchWithAuth(`/api/bank-connections/${connectionId}`, { method: "DELETE" });
}

export async function getBankTransactions(
  connectionId: string,
  page = 0
): Promise<PulledTransaction[]> {
  return fetchWithAuth(`/api/bank-connections/${connectionId}/transactions?page=${page}`);
}

export async function pushBankToLedger(connectionId: string): Promise<{ pushed: number }> {
  return fetchWithAuth(`/api/bank-connections/${connectionId}/push-to-ledger`, { method: "POST" });
}

export async function registerInCarpcoOs(connectionId: string): Promise<{ ok: boolean; accountName: string | null }> {
  return fetchWithAuth(`/api/bank-connections/${connectionId}/register-in-carpcos`, { method: "POST" });
}

export async function setCarpcoOsClientId(connectionId: string, carpcoOsClientId: string): Promise<void> {
  return fetchWithAuth(`/api/bank-connections/${connectionId}/carpcos-client`, {
    method: "PATCH",
    body: JSON.stringify({ carpco_os_client_id: carpcoOsClientId }),
  });
}

export async function searchCarpcoOsClients(q: string): Promise<{ id: string; name: string }[]> {
  return fetchWithAuth(`/api/carpcos/clients/search?q=${encodeURIComponent(q)}`);
}

export interface TellerAccountOption {
  id: string;
  name: string;
  subtype: string;
  last_four: string;
  institution: string;
  current: boolean;
  tracked: boolean;
}

export async function getTellerAccountOptions(connectionId: string): Promise<TellerAccountOption[]> {
  return fetchWithAuth(`/api/bank-connections/${connectionId}/teller-accounts`);
}

export async function setTellerAccount(connectionId: string, tellerAccountId: string): Promise<void> {
  return fetchWithAuth(`/api/bank-connections/${connectionId}/teller-account`, {
    method: "PATCH",
    body: JSON.stringify({ teller_account_id: tellerAccountId }),
  });
}

export async function addTellerAccount(
  connectionId: string,
  tellerAccountId: string,
  financialSourceId?: string
): Promise<{ ok: boolean; id: string }> {
  return fetchWithAuth(`/api/bank-connections/${connectionId}/add-account`, {
    method: "POST",
    body: JSON.stringify({ teller_account_id: tellerAccountId, financial_source_id: financialSourceId }),
  });
}

export async function getLiveBankBalance(
  connectionId: string
): Promise<{ available: string; ledger: string }> {
  return fetchWithAuth(`/api/bank-connections/${connectionId}/balance`);
}

export interface AccountIntegration {
  id: string;
  integration_slug: string;
  enabled: boolean;
  created_at: string;
  /** True when an API key is stored. Forward-only clients connect with no key. */
  has_api_key?: boolean;
}

export async function getAccountIntegration(accountId: string, slug: string): Promise<AccountIntegration | null> {
  try {
    return await fetchWithAuth<AccountIntegration>(`/api/accounts/${accountId}/integrations/${slug}`);
  } catch {
    return null;
  }
}

export async function saveAccountIntegration(accountId: string, slug: string, apiKey: string): Promise<AccountIntegration> {
  return fetchWithAuth<AccountIntegration>(`/api/accounts/${accountId}/integrations/${slug}`, {
    method: "POST",
    body: JSON.stringify({ api_key: apiKey }),
  });
}

export async function deleteAccountIntegration(accountId: string, slug: string): Promise<void> {
  return fetchWithAuth<void>(`/api/accounts/${accountId}/integrations/${slug}`, { method: "DELETE" });
}

export interface MakersHubStats {
  available: boolean;
  total?: number;
  pending?: number;
  approved?: number;
  rejected?: number;
  overdue?: number;
  pendingAmount?: number;
}

export async function getMakersHubStats(accountId: string): Promise<MakersHubStats> {
  return fetchWithAuth<MakersHubStats>(`/api/accounts/${accountId}/makershub/stats`);
}

// MakersHub invoice forwarding (D1)
export async function getMakersHubForwardAddress(accountId: string): Promise<{ forward_email: string | null }> {
  return fetchWithAuth<{ forward_email: string | null }>(`/api/accounts/${accountId}/makershub/forward-address`);
}

export async function setMakersHubForwardAddress(accountId: string, forwardEmail: string): Promise<{ forward_email: string | null }> {
  return fetchWithAuth<{ forward_email: string | null }>(`/api/accounts/${accountId}/makershub/forward-address`, {
    method: "PUT",
    body: JSON.stringify({ forward_email: forwardEmail }),
  });
}

export async function forwardInvoiceToMakersHub(args: {
  accountId: string;
  mailbox: string;
  message_id: string;
  subject?: string;
}): Promise<{ ok: boolean; forwarded_to: string }> {
  return fetchWithAuth<{ ok: boolean; forwarded_to: string }>(`/api/accounts/${args.accountId}/makershub/forward-invoice`, {
    method: "POST",
    body: JSON.stringify({ mailbox: args.mailbox, message_id: args.message_id, subject: args.subject }),
  });
}

export async function submitToMakersHub(accountId: string, data: string, filename: string, type: string): Promise<{ invoiceId: number }> {
  return fetchWithAuth<{ invoiceId: number }>(`/api/accounts/${accountId}/makershub/submit-invoice`, {
    method: "POST",
    body: JSON.stringify({ data, filename, type }),
  });
}
